import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { settingsAPI, servicesAPI, getErrorMessage, API_BASE_URL } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, RotateCcw, History, CreditCard, Wallet, Calendar, Shield, ShieldCheck, Settings, Clock, Eye, AlertTriangle, Building, User, MapPin, Store, Warehouse, Package, Percent, FlaskConical, Hash, Database } from 'lucide-react';
import { formatDateTime, formatDisplayCode, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import VATConfigCard from '@/components/VATConfigCard';
import { ServiceQATab } from '@/components/settings/ServiceQATab';
import { RBACCoverageTab } from '@/components/settings/RBACCoverageTab';
import { DocumentPrefixesTab } from '@/components/settings/DocumentPrefixesTab';
import { PaymentsHubTab } from '@/components/settings/PaymentsHubTab';
import { DataMigrationTab } from '@/components/settings/DataMigrationTab';
import { PrintPresetsUnifiedCockpit } from '@/components/settings/PrintPresetsUnifiedCockpit';
import { TemplateStoredEffectiveComparator } from '@/components/settings/TemplateStoredEffectiveComparator';
import { ErpCatalogPanel } from '@/components/template-builder/ErpCatalogPanel';
import { TemplateCockpitDiagnosticsPanel } from '@/components/template-builder/TemplateCockpitDiagnosticsPanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { safeRequest } from '@/lib/safeRequest';
import { buildAuthHeaders } from '@/lib/authStorage';

const EMPTY_PRINT_BANKING_FORM = {
  bank_account_holder: '',
  bank_name: '',
  bank_account_number: '',
  bank_iban: '',
  bank_nib: '',
  bank_swift: '',
  additional_banks: [],
};

const createEmptyAdditionalBank = (index = 0) => ({
  label: `Banco ${index + 2}`,
  bank_account_holder: '',
  bank_name: '',
  bank_account_number: '',
  bank_iban: '',
  bank_nib: '',
  bank_swift: '',
});

// Location Types
const LOCATION_TYPES = [
  { value: 'store', label: 'Loja', icon: Store },
  { value: 'warehouse', label: 'Armazém', icon: Warehouse },
  { value: 'showcase', label: 'Vitrine', icon: Eye },
  { value: 'repair', label: 'Reparação', icon: Settings },
  { value: 'transit', label: 'Trânsito', icon: Package },
];

// Payment Method Types
const PAYMENT_METHOD_TYPES = [
  { value: 'cash', label: 'Numerário' },
  { value: 'bank_transfer', label: 'Transferência Bancária' },
  { value: 'pos', label: 'POS' },
  { value: 'mobile_money', label: 'Dinheiro Móvel' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Outro' },
];

// Account Owners
const ACCOUNT_OWNERS = [
  { value: 'CG Business', label: 'CG Business' },
  { value: 'Charles Gabell', label: 'Charles Gabell' },
  { value: 'Anzima Adamo', label: 'Anzima Adamo' },
];

const ADMIN_SETTINGS_TABS = [
  { value: 'locations', label: 'Locais', icon: MapPin, testId: 'tab-locations' },
  { value: 'payments-hub', label: 'Pagamentos', icon: Wallet, testId: 'tab-payments-hub' },
  { value: 'payment-conditions', label: 'Condições', icon: Shield, testId: 'tab-payment-conditions' },
  { value: 'due-date-options', label: 'Vencimentos', icon: Calendar, testId: 'tab-due-dates' },
  { value: 'warranty-options', label: 'Garantias', icon: Clock, testId: 'tab-warranties' },
  { value: 'document-prefixes', label: 'Prefixos Docs', icon: Hash, testId: 'tab-document-prefixes' },
  { value: 'print-settings', label: 'Impressão', icon: Settings, testId: 'tab-print-settings' },
  { value: 'vat', label: 'IVA', icon: Percent, testId: 'tab-vat' },
  { value: 'audit', label: 'Auditoria', icon: History, testId: 'tab-audit' },
  { value: 'rbac-coverage', label: 'Cobertura RBAC', icon: ShieldCheck, testId: 'tab-rbac-coverage' },
  { value: 'service-qa', label: 'QA Serviços', icon: FlaskConical, testId: 'tab-service-qa' },
];

const LEGACY_SETTINGS_REDIRECTS = {
  employees: 'employees',
  'roles-permissions': 'roles',
};

const QA_ONLY_SETTINGS_TAB = [
  { value: 'service-qa', label: 'QA Serviços', icon: FlaskConical, testId: 'tab-service-qa' },
];

const MIGRATION_SETTINGS_TAB = [
  { value: 'data-migration', label: 'Migração', icon: Database, testId: 'tab-data-migration' },
];

const buildFetchRequestError = async (response, fallbackMessage) => {
  const contentType = response.headers.get('content-type') || '';
  let detail = '';

  try {
    if (contentType.includes('application/json')) {
      const payload = await response.json();
      detail = payload?.detail || payload?.message || '';
    } else {
      detail = (await response.text())?.trim?.() || '';
    }
  } catch {
    detail = '';
  }

  const error = new Error(detail || fallbackMessage || `HTTP ${response.status}`);
  error.response = {
    status: response.status,
    data: {
      detail: detail || fallbackMessage || `HTTP ${response.status}`,
    },
  };
  return error;
};

const fetchJsonWithAuth = async (path, init = {}, fallbackMessage) => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: buildAuthHeaders(init.headers || {}),
  });

  if (!response.ok) {
    throw await buildFetchRequestError(response, fallbackMessage);
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return null;
  }

  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

export default function SettingsPage() {
  const { hasRole, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = hasRole(['admin']);
  const canManageServiceQA = hasRole(['admin', 'store_manager']);
  const canAccessMigration = isAdmin || hasPermission(['migration.view', 'migration.export', 'migration.import']);
  const canAccessSettings = isAdmin || canManageServiceQA || canAccessMigration;
  const settingsTabStorageKey = 'cg-business-settings-active-tab';
  const requestedTab = searchParams.get('tab');
  const storedSettingsTab = (() => {
    try {
      return requestedTab || sessionStorage.getItem(settingsTabStorageKey) || 'locations';
    } catch {
      return requestedTab || 'locations';
    }
  })();
  const availableTabs = useMemo(() => {
    const tabs = [];
    if (isAdmin) {
      tabs.push(...ADMIN_SETTINGS_TABS);
    } else if (canManageServiceQA) {
      tabs.push(...QA_ONLY_SETTINGS_TAB);
    }
    if (canAccessMigration) {
      tabs.push(...MIGRATION_SETTINGS_TAB);
    }
    return tabs.length ? tabs : MIGRATION_SETTINGS_TAB;
  }, [canAccessMigration, canManageServiceQA, isAdmin]);
  const availableTabValues = useMemo(() => availableTabs.map((tab) => tab.value), [availableTabs]);

  const resolveValidSettingsTab = useCallback((candidate) => {
    if (candidate && availableTabValues.includes(candidate)) return candidate;
    return availableTabs[0]?.value || 'service-qa';
  }, [availableTabValues, availableTabs]);
  
  const [activeTab, setActiveTab] = useState(storedSettingsTab);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  
  // Data states
  const [locations, setLocations] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [paymentConditions, setPaymentConditions] = useState([]);
  const [dueDateOptions, setDueDateOptions] = useState([]);
  const [warrantyOptions, setWarrantyOptions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [serviceQaScenarios, setServiceQaScenarios] = useState([]);
  const [serviceQaRuns, setServiceQaRuns] = useState([]);
  const [serviceQaRunningKey, setServiceQaRunningKey] = useState('');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogEntityType, setDialogEntityType] = useState('');
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  
  // Edit justification dialog
  const [justificationDialogOpen, setJustificationDialogOpen] = useState(false);
  const [editJustification, setEditJustification] = useState('');
  const [pendingEditData, setPendingEditData] = useState(null);
  
  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  
  // Sales history dialog (for payment accounts)
  const [salesHistoryDialogOpen, setSalesHistoryDialogOpen] = useState(false);
  const [salesHistoryData, setSalesHistoryData] = useState(null);
  
  // Location stock summary dialog
  const [stockSummaryDialogOpen, setStockSummaryDialogOpen] = useState(false);
  const [stockSummaryData, setStockSummaryData] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({});
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  const [loadedTabsState, setLoadedTabsState] = useState({});

  const buildSettingsRequestOptions = useCallback((endpoint, overrides = {}) => ({
    timeoutMs: 25000,
    retries: 1,
    required: true,
    telemetry: { module: 'settings', endpoint, onEvent: captureFetchTelemetry },
    ...overrides,
  }), [captureFetchTelemetry]);

  const markTabAsLoaded = useCallback((tabKey) => {
    setLoadedTabsState((prev) => ({ ...prev, [tabKey]: true }));
  }, []);

  const clearLoadedTabsState = useCallback(() => {
    setLoadedTabsState({});
  }, []);

  const loadServiceQaData = useCallback(async (forceReload = false) => {
    if (!canManageServiceQA) return;
    const qaCacheKey = `service-qa:${showDeleted ? 'deleted' : 'active'}`;
    if (!forceReload && loadedTabsState[qaCacheKey] && (serviceQaScenarios.length > 0 || serviceQaRuns.length > 0)) {
      return;
    }

    const [scenariosResponse, runsResponse] = await Promise.all([
      safeRequest(() => servicesAPI.listQaScenarios(), { data: { scenarios: [] } }, buildSettingsRequestOptions('services.qa-scenarios')),
      safeRequest(() => servicesAPI.listQaRuns(20), { data: { runs: [] } }, buildSettingsRequestOptions('services.qa-runs')),
    ]);
    setServiceQaScenarios(scenariosResponse.data?.scenarios || []);
    setServiceQaRuns(runsResponse.data?.runs || []);
    markTabAsLoaded(qaCacheKey);
  }, [buildSettingsRequestOptions, canManageServiceQA, loadedTabsState, markTabAsLoaded, serviceQaRuns.length, serviceQaScenarios.length, showDeleted]);
  
  // Load all data
  const loadAllData = useCallback(async (forceReload = false) => {
    setLoading(true);
    try {
      resetFetchTelemetry();
      const tabCacheKey = `${activeTab}:${showDeleted ? 'deleted' : 'active'}`;
      if (!forceReload && loadedTabsState[tabCacheKey]) {
        return;
      }

      if (activeTab === 'vat' || activeTab === 'data-migration' || activeTab === 'document-prefixes' || activeTab === 'rbac-coverage') {
        return;
      }

      if (activeTab === 'service-qa') {
        await loadServiceQaData(forceReload);
        return;
      }

      if (!isAdmin) {
        return;
      }

      switch (activeTab) {
        case 'locations':
        case 'print-settings': {
          if (activeTab === 'print-settings' && locations.length > 0) {
            markTabAsLoaded(tabCacheKey);
            break;
          }

          const locs = await safeRequest(
            () => settingsAPI.listLocations(showDeleted),
            { data: [] },
            buildSettingsRequestOptions('settings.locations'),
          );
          setLocations(locs.data || []);
          markTabAsLoaded(tabCacheKey);
          break;
        }
        case 'payments-hub': {
          const [methods, accounts] = await Promise.all([
            safeRequest(() => settingsAPI.listPaymentMethods(showDeleted), { data: [] }, buildSettingsRequestOptions('settings.payment-methods')),
            safeRequest(() => settingsAPI.listPaymentAccounts(showDeleted), { data: [] }, buildSettingsRequestOptions('settings.payment-accounts')),
          ]);
          setPaymentMethods(methods.data || []);
          setPaymentAccounts(accounts.data || []);
          markTabAsLoaded(tabCacheKey);
          break;
        }
        case 'payment-conditions': {
          const conditions = await safeRequest(
            () => settingsAPI.listPaymentConditions(showDeleted),
            { data: [] },
            buildSettingsRequestOptions('settings.payment-conditions'),
          );
          setPaymentConditions(conditions.data || []);
          markTabAsLoaded(tabCacheKey);
          break;
        }
        case 'due-date-options': {
          const dueDates = await safeRequest(
            () => settingsAPI.listDueDateOptions(showDeleted),
            { data: [] },
            buildSettingsRequestOptions('settings.due-date-options'),
          );
          setDueDateOptions(dueDates.data || []);
          markTabAsLoaded(tabCacheKey);
          break;
        }
        case 'warranty-options': {
          const warranties = await safeRequest(
            () => settingsAPI.listWarrantyOptions(showDeleted),
            { data: [] },
            buildSettingsRequestOptions('settings.warranty-options'),
          );
          setWarrantyOptions(warranties.data || []);
          markTabAsLoaded(tabCacheKey);
          break;
        }
        case 'audit': {
          const [locs, logs] = await Promise.all([
            safeRequest(() => settingsAPI.listLocations(showDeleted), { data: [] }, buildSettingsRequestOptions('settings.locations')),
            safeRequest(() => settingsAPI.getAuditLogs(null, 50), { data: [] }, buildSettingsRequestOptions('settings.audit-logs')),
          ]);
          setLocations(locs.data || []);
          setAuditLogs(logs.data || []);
          markTabAsLoaded(tabCacheKey);
          break;
        }
        default:
          break;
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar dados'));
    } finally {
      setLoading(false);
    }
  }, [activeTab, buildSettingsRequestOptions, canManageServiceQA, isAdmin, loadServiceQaData, loadedTabsState, locations.length, markTabAsLoaded, resetFetchTelemetry, showDeleted]);
  
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  useEffect(() => {
    clearLoadedTabsState();
  }, [showDeleted, clearLoadedTabsState]);

  const hasActiveTabData = useMemo(() => {
    switch (activeTab) {
      case 'locations':
        return locations.length > 0;
      case 'payments-hub':
        return paymentMethods.length > 0 || paymentAccounts.length > 0;
      case 'payment-conditions':
        return paymentConditions.length > 0;
      case 'due-date-options':
        return dueDateOptions.length > 0;
      case 'warranty-options':
        return warrantyOptions.length > 0;
      case 'audit':
        return auditLogs.length > 0;
      default:
        return true;
    }
  }, [activeTab, auditLogs.length, dueDateOptions.length, locations.length, paymentAccounts.length, paymentConditions.length, paymentMethods.length, warrantyOptions.length]);

  const settingsEmptyStateMeta = useMemo(() => {
    const metaByTab = {
      locations: {
        title: 'Sem locais carregados',
        description: 'A secção Locais abriu sem conteúdo. Recarregue para voltar a ler as localizações do backend.',
      },
      'payments-hub': {
        title: 'Sem dados de pagamentos',
        description: 'A secção Pagamentos abriu sem métodos nem contas. Recarregue para voltar a consultar o backend.',
      },
      'payment-conditions': {
        title: 'Sem condições carregadas',
        description: 'A secção Condições abriu sem conteúdo. Recarregue para voltar a buscar os dados.',
      },
      'due-date-options': {
        title: 'Sem vencimentos carregados',
        description: 'A secção Vencimentos abriu sem conteúdo. Recarregue para voltar a buscar os dados.',
      },
      'warranty-options': {
        title: 'Sem garantias carregadas',
        description: 'A secção Garantias abriu sem conteúdo. Recarregue para voltar a buscar os dados.',
      },
      audit: {
        title: 'Sem auditoria carregada',
        description: 'A secção Auditoria abriu sem conteúdo. Recarregue para voltar a consultar o histórico.',
      },
    };

    return metaByTab[activeTab] || {
      title: 'Sem dados disponíveis nesta secção',
      description: 'Recarregue a secção para tentar novamente a leitura do backend.',
    };
  }, [activeTab]);

  useEffect(() => {
    if (requestedTab && LEGACY_SETTINGS_REDIRECTS[requestedTab]) {
      navigate(`/admin-center?tab=${LEGACY_SETTINGS_REDIRECTS[requestedTab]}`, { replace: true });
    }
  }, [navigate, requestedTab]);

  useEffect(() => {
    const nextTab = resolveValidSettingsTab(activeTab);
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, resolveValidSettingsTab]);

  useEffect(() => {
    sessionStorage.setItem(settingsTabStorageKey, resolveValidSettingsTab(activeTab));
  }, [activeTab, resolveValidSettingsTab]);

  useEffect(() => {
    const nextTab = resolveValidSettingsTab(requestedTab || storedSettingsTab);
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  // only react to explicit external URL changes, not to our own tab clicks
  // to avoid router churn and tab flicker on heavy settings panels.
  }, [requestedTab, storedSettingsTab, activeTab, resolveValidSettingsTab]);

  const handleSettingsTabChange = (nextTab) => {
    const safeTab = resolveValidSettingsTab(nextTab);
    setActiveTab(safeTab);

    try {
      sessionStorage.setItem(settingsTabStorageKey, safeTab);
    } catch (error) {
      console.error('Error persisting settings tab:', error);
    }

    const currentTab = searchParams.get('tab');
    if (currentTab === safeTab) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', safeTab);
    setSearchParams(nextParams, { replace: true });
  };

  const handleRunServiceQaScenario = async (scenarioKey) => {
    try {
      setServiceQaRunningKey(scenarioKey);
      const response = await servicesAPI.runQaScenario({ scenario_key: scenarioKey });
      const run = response.data?.run;
      toast.success(run?.scenario_label ? `${run.scenario_label} criado com sucesso.` : 'Cenário QA executado com sucesso.');
      clearLoadedTabsState();
      await loadServiceQaData(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao executar cenário QA'));
    } finally {
      setServiceQaRunningKey('');
    }
  };
  
  // Seed initial data
  const handleSeedData = async () => {
    try {
      const response = await settingsAPI.seedInitialData();
      toast.success('Dados iniciais criados com sucesso!');
      clearLoadedTabsState();
      loadAllData(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao criar dados iniciais'));
    }
  };
  
  // Generic CRUD handlers
  const handleCreate = (entityType = activeTab) => {
    setEditMode(false);
    setSelectedItem(null);
    setDialogEntityType(entityType);
    setFormData(getInitialFormData(entityType));
    setDialogOpen(true);
  };
  
  const handleEdit = (item, entityType = activeTab) => {
    setEditMode(true);
    setSelectedItem(item);
    setDialogEntityType(entityType);
    setFormData(getFormDataFromItem(item, entityType));
    setDialogOpen(true);
  };
  
  const performDeleteRequest = async (entityType, itemId, reason) => {
    switch (entityType) {
      case 'locations':
        return settingsAPI.deleteLocation(itemId, reason);
      case 'payment-methods':
        return settingsAPI.deletePaymentMethod(itemId, reason);
      case 'payment-accounts':
        return settingsAPI.deletePaymentAccount(itemId, reason);
      case 'payment-conditions':
        return settingsAPI.deletePaymentCondition(itemId, reason);
      case 'due-date-options':
        return settingsAPI.deleteDueDateOption(itemId, reason);
      case 'warranty-options':
        return settingsAPI.deleteWarrantyOption(itemId, reason);
      default:
        return null;
    }
  };

  const handleDelete = async (item, entityType = activeTab) => {
    if (isAdmin) {
      try {
        await performDeleteRequest(entityType, item.id, 'Eliminação directa pelo administrador');
        toast.success('Registo eliminado pelo administrador');
        clearLoadedTabsState();
        loadAllData(true);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Erro ao eliminar'));
      }
      return;
    }

    setSelectedItem(item);
    setDialogEntityType(entityType);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };
  
  const getInitialFormData = (entityType) => {
    switch (entityType) {
      case 'locations':
        return { name: '', type: 'store', address: '', notes: '' };
      case 'payment-methods':
        return { name: '', type: 'cash', notes: '', is_active: true };
      case 'payment-accounts':
        return { name: '', payment_method_id: '', owner: 'CG Business', notes: '' };
      case 'payment-conditions':
        return { name: '', description: '' };
      case 'due-date-options':
        return { name: '', days: '' };
      case 'warranty-options':
        return { name: '', days: '' };
      default:
        return {};
    }
  };
  
  const getFormDataFromItem = (item, entityType) => {
    switch (entityType) {
      case 'locations':
        return { 
          name: item.name, 
          type: item.type, 
          address: item.address || '', 
          notes: item.notes || '',
          is_active: item.is_active ?? true
        };
      case 'payment-methods':
        return {
          name: item.name,
          type: item.type,
          notes: item.notes || '',
          is_active: item.is_active ?? true,
        };
      case 'payment-accounts':
        return { 
          name: item.name, 
          payment_method_id: item.payment_method_id, 
          owner: item.owner || 'CG Business', 
          notes: item.notes || '' 
        };
      case 'payment-conditions':
        return { name: item.name, description: item.description || '', is_active: item.is_active ?? true };
      case 'due-date-options':
        return { name: item.name, days: item.days ?? '', is_active: item.is_active ?? true };
      case 'warranty-options':
        return { name: item.name, days: item.days ?? '', is_active: item.is_active ?? true };
      default:
        return {};
    }
  };
  
  const handleSave = async () => {
    const entityType = dialogEntityType || activeTab;
    if (editMode) {
      setPendingEditData({ ...formData });
      setEditJustification('');
      setJustificationDialogOpen(true);
      return;
    }
    
    try {
      const data = prepareDataForSave(entityType, formData);
      
      switch (entityType) {
        case 'locations':
          await settingsAPI.createLocation(data);
          toast.success('Localização criada!');
          break;
        case 'payment-methods':
          await settingsAPI.createPaymentMethod(data);
          toast.success('Forma de pagamento criada!');
          break;
        case 'payment-accounts':
          await settingsAPI.createPaymentAccount(data);
          toast.success('Conta de pagamento criada!');
          break;
        case 'payment-conditions':
          await settingsAPI.createPaymentCondition(data);
          toast.success('Condição de pagamento criada!');
          break;
        case 'due-date-options':
          await settingsAPI.createDueDateOption(data);
          toast.success('Opção de vencimento criada!');
          break;
        case 'warranty-options':
          await settingsAPI.createWarrantyOption(data);
          toast.success('Opção de garantia criada!');
          break;
        default:
          break;
      }
      
      setDialogOpen(false);
      setDialogEntityType('');
      clearLoadedTabsState();
      loadAllData(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao criar'));
    }
  };
  
  const prepareDataForSave = (entityType, data) => {
    const cleaned = { ...data };
    
    // Convert empty strings to null for optional fields
    Object.keys(cleaned).forEach(key => {
      if (cleaned[key] === '') {
        if (key === 'days') {
          cleaned[key] = null;
        } else if (key !== 'name') {
          cleaned[key] = null;
        }
      }
    });
    
    // Convert days to number if present
    if (cleaned.days && cleaned.days !== '') {
      cleaned.days = parseInt(cleaned.days, 10);
    }
    
    return cleaned;
  };
  
  const handleConfirmEdit = async () => {
    const entityType = dialogEntityType || activeTab;
    if (!editJustification || editJustification.trim().length < 5) {
      toast.error('Justificação obrigatória (mínimo 5 caracteres)');
      return;
    }
    
    try {
      const data = prepareDataForSave(entityType, pendingEditData);
      
      switch (entityType) {
        case 'locations':
          await settingsAPI.updateLocation(selectedItem.id, data, editJustification);
          toast.success('Localização actualizada!');
          break;
        case 'payment-methods':
          await settingsAPI.updatePaymentMethod(selectedItem.id, data, editJustification);
          toast.success('Forma de pagamento actualizada!');
          break;
        case 'payment-accounts':
          await settingsAPI.updatePaymentAccount(selectedItem.id, data, editJustification);
          toast.success('Conta de pagamento actualizada!');
          break;
        case 'payment-conditions':
          await settingsAPI.updatePaymentCondition(selectedItem.id, data, editJustification);
          toast.success('Condição de pagamento actualizada!');
          break;
        case 'due-date-options':
          await settingsAPI.updateDueDateOption(selectedItem.id, data, editJustification);
          toast.success('Opção de vencimento actualizada!');
          break;
        case 'warranty-options':
          await settingsAPI.updateWarrantyOption(selectedItem.id, data, editJustification);
          toast.success('Opção de garantia actualizada!');
          break;
        default:
          break;
      }
      
      setJustificationDialogOpen(false);
      setDialogOpen(false);
      setDialogEntityType('');
      clearLoadedTabsState();
      loadAllData(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao actualizar'));
    }
  };
  
  const handleConfirmDelete = async () => {
    const entityType = dialogEntityType || activeTab;
    if (!deleteReason || deleteReason.trim().length < 5) {
      toast.error('Motivo obrigatório (mínimo 5 caracteres)');
      return;
    }
    
    try {
      await performDeleteRequest(entityType, selectedItem.id, deleteReason);
      toast.success('Registo eliminado com sucesso!');
      
      setDeleteDialogOpen(false);
      setDialogEntityType('');
      clearLoadedTabsState();
      loadAllData(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar'));
    }
  };
  
  const handleRestore = async (item, entityType = activeTab) => {
    try {
      switch (entityType) {
        case 'locations':
          await settingsAPI.restoreLocation(item.id);
          toast.success('Localização restaurada!');
          break;
        case 'payment-methods':
          await settingsAPI.restorePaymentMethod(item.id);
          toast.success('Forma de pagamento restaurada!');
          break;
        case 'payment-accounts':
          await settingsAPI.restorePaymentAccount(item.id);
          toast.success('Conta de pagamento restaurada!');
          break;
        case 'payment-conditions':
          await settingsAPI.restorePaymentCondition(item.id);
          toast.success('Condição de pagamento restaurada!');
          break;
        case 'due-date-options':
          await settingsAPI.restoreDueDateOption(item.id);
          toast.success('Opção de vencimento restaurada!');
          break;
        case 'warranty-options':
          await settingsAPI.restoreWarrantyOption(item.id);
          toast.success('Opção de garantia restaurada!');
          break;
        default:
          break;
      }
      
      clearLoadedTabsState();
      loadAllData(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao restaurar'));
    }
  };
  
  const handleViewSalesHistory = async (account) => {
    try {
      const response = await settingsAPI.getPaymentAccountSalesHistory(account.id);
      setSalesHistoryData(response.data);
      setSalesHistoryDialogOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar histórico'));
    }
  };
  
  const handleViewStockSummary = async (location) => {
    try {
      const response = await settingsAPI.getLocationStockSummary(location.id);
      setStockSummaryData(response.data);
      setStockSummaryDialogOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar resumo de stock'));
    }
  };
  
  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'locations':
        return <LocationsTab 
          data={locations} 
          onEdit={(item) => handleEdit(item, 'locations')}
          onDelete={(item) => handleDelete(item, 'locations')}
          onRestore={(item) => handleRestore(item, 'locations')}
          onViewStock={handleViewStockSummary}
          isAdmin={isAdmin}
        />;
      case 'payments-hub':
        return <PaymentsHubTab
          paymentMethods={paymentMethods}
          paymentAccounts={paymentAccounts}
          onCreateMethod={() => handleCreate('payment-methods')}
          onCreateAccount={() => handleCreate('payment-accounts')}
          onEditMethod={(item) => handleEdit(item, 'payment-methods')}
          onEditAccount={(item) => handleEdit(item, 'payment-accounts')}
          onDeleteMethod={(item) => handleDelete(item, 'payment-methods')}
          onDeleteAccount={(item) => handleDelete(item, 'payment-accounts')}
          onRestoreMethod={(item) => handleRestore(item, 'payment-methods')}
          onRestoreAccount={(item) => handleRestore(item, 'payment-accounts')}
          onViewAccountHistory={handleViewSalesHistory}
          isAdmin={isAdmin}
        />;
      case 'payment-methods':
        return <PaymentMethodsTab 
          data={paymentMethods} 
          onEdit={(item) => handleEdit(item, 'payment-methods')}
          onDelete={(item) => handleDelete(item, 'payment-methods')}
          onRestore={(item) => handleRestore(item, 'payment-methods')}
          isAdmin={isAdmin}
        />;
      case 'payment-accounts':
        return <PaymentAccountsTab 
          data={paymentAccounts}
          paymentMethods={paymentMethods.filter(m => m.is_active && !m.is_deleted)}
          onEdit={(item) => handleEdit(item, 'payment-accounts')}
          onDelete={(item) => handleDelete(item, 'payment-accounts')}
          onRestore={(item) => handleRestore(item, 'payment-accounts')}
          onViewHistory={handleViewSalesHistory}
          isAdmin={isAdmin}
        />;
      case 'payment-conditions':
        return <PaymentConditionsTab 
          data={paymentConditions}
          onEdit={(item) => handleEdit(item, 'payment-conditions')}
          onDelete={(item) => handleDelete(item, 'payment-conditions')}
          onRestore={(item) => handleRestore(item, 'payment-conditions')}
          isAdmin={isAdmin}
        />;
      case 'due-date-options':
        return <DueDateOptionsTab 
          data={dueDateOptions}
          onEdit={(item) => handleEdit(item, 'due-date-options')}
          onDelete={(item) => handleDelete(item, 'due-date-options')}
          onRestore={(item) => handleRestore(item, 'due-date-options')}
          isAdmin={isAdmin}
        />;
      case 'warranty-options':
        return <WarrantyOptionsTab 
          data={warrantyOptions}
          onEdit={(item) => handleEdit(item, 'warranty-options')}
          onDelete={(item) => handleDelete(item, 'warranty-options')}
          onRestore={(item) => handleRestore(item, 'warranty-options')}
          isAdmin={isAdmin}
        />;
      case 'audit':
        return <AuditLogsTab data={auditLogs} />;
      case 'rbac-coverage':
        return <RBACCoverageTab />;
      case 'document-prefixes':
        return <DocumentPrefixesTab />;
      case 'print-settings':
        return <PrintSettingsTab locations={locations.filter(l => l.is_active && !l.is_deleted)} />;
      case 'vat':
        return <VATConfigCard />;
      case 'service-qa':
        return (
          <ServiceQATab
            scenarios={serviceQaScenarios}
            runs={serviceQaRuns}
            loading={loading}
            runningScenarioKey={serviceQaRunningKey}
            onRefresh={loadServiceQaData}
            onRunScenario={handleRunServiceQaScenario}
          />
        );
      case 'data-migration':
        return <DataMigrationTab canExport={isAdmin || hasPermission(['migration.export'])} canImport={isAdmin || hasPermission(['migration.import'])} />;
      default:
        return null;
    }
  };
  
  // Render form dialog content
  const renderFormContent = () => {
    const entityType = dialogEntityType || activeTab;
    switch (entityType) {
      case 'locations':
        return <LocationForm formData={formData} setFormData={setFormData} editMode={editMode} />;
      case 'payment-methods':
        return <PaymentMethodForm formData={formData} setFormData={setFormData} editMode={editMode} />;
      case 'payment-accounts':
        return <PaymentAccountForm 
          formData={formData} 
          setFormData={setFormData} 
          paymentMethods={paymentMethods.filter(m => m.is_active && !m.is_deleted)}
        />;
      case 'payment-conditions':
        return <PaymentConditionForm formData={formData} setFormData={setFormData} editMode={editMode} />;
      case 'due-date-options':
        return <DueDateOptionForm formData={formData} setFormData={setFormData} editMode={editMode} />;
      case 'warranty-options':
        return <WarrantyOptionForm formData={formData} setFormData={setFormData} editMode={editMode} />;
      default:
        return null;
    }
  };
  
  const getDialogTitle = () => {
    const action = editMode ? 'Editar' : 'Nova';
    const entityType = dialogEntityType || activeTab;
    switch (entityType) {
      case 'locations': return `${action} Localização`;
      case 'payment-methods': return `${action} Forma de Pagamento`;
      case 'payment-accounts': return `${action} Conta de Pagamento`;
      case 'payment-conditions': return `${action} Condição de Pagamento`;
      case 'due-date-options': return `${action === 'Nova' ? 'Novo' : 'Editar'} Vencimento`;
      case 'warranty-options': return `${action} Garantia`;
      default: return '';
    }
  };
  
  if (!canAccessSettings) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Acesso Restrito
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Esta página está disponível apenas para administradores e perfis autorizados. Contacte o administrador do sistema para acesso.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3" style={{fontFamily: 'Manrope'}}>
            <Settings className="h-8 w-8 text-primary" />
            Configurações
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerir configurações do sistema (formas de pagamento, contas, locais, etc.)
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                id="show-deleted"
                checked={showDeleted}
                onCheckedChange={setShowDeleted}
              />
              <Label htmlFor="show-deleted" className="text-sm text-muted-foreground">
                Mostrar eliminados
              </Label>
            </div>
            <Button variant="outline" onClick={handleSeedData} data-testid="seed-data-btn">
              <Plus className="mr-2 h-4 w-4" />
              Criar Dados Iniciais
            </Button>
          </div>
        )}
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleSettingsTabChange} className="space-y-4" data-testid="settings-tabs-root">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-muted/50 p-1">
          {availableTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="flex items-center gap-2 data-[state=active]:bg-background"
                data-testid={tab.testId}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
        
        {/* Tab Content */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between py-4">
            <div>
              <CardTitle className="text-lg">{getTabTitle(activeTab)}</CardTitle>
              <CardDescription>{getTabDescription(activeTab)}</CardDescription>
            </div>
            {isAdmin && activeTab !== 'audit' && activeTab !== 'payments-hub' && activeTab !== 'rbac-coverage' && activeTab !== 'document-prefixes' && activeTab !== 'print-settings' && activeTab !== 'vat' && activeTab !== 'service-qa' && activeTab !== 'data-migration' && (
              <Button onClick={() => handleCreate(activeTab)} data-testid={`create-${activeTab}-btn`}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <ModuleStateBoundary
              loading={loading}
              hasData={hasActiveTabData || partialIssues.length > 0}
              partialIssues={partialIssues}
              loadingTestId="settings-tab-loading"
              partialIssuesTestId="settings-tab-partial-issues"
              emptyState={
                <EmptyStatePanel
                  title={settingsEmptyStateMeta.title}
                  description={settingsEmptyStateMeta.description}
                  actionLabel="Recarregar secção"
                  onAction={() => loadAllData(true)}
                  testId={`settings-empty-state-${activeTab}`}
                />
              }
            >
              {renderTabContent()}
            </ModuleStateBoundary>
          </CardContent>
        </Card>
      </Tabs>
      
      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
            <DialogDescription>
              {editMode ? 'Edite os campos necessários abaixo.' : 'Preencha os campos abaixo para criar um novo registo.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {renderFormContent()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} data-testid="save-entity-btn">
              {editMode ? 'Guardar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Justification Dialog */}
      <Dialog open={justificationDialogOpen} onOpenChange={setJustificationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Justificação Obrigatória</DialogTitle>
            <DialogDescription>
              Por favor, forneça uma justificação para esta alteração. Esta informação será registada no histórico de auditoria.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={editJustification}
              onChange={(e) => setEditJustification(e.target.value)}
              placeholder="Motivo da alteração (mínimo 5 caracteres)..."
              className="min-h-[100px]"
              data-testid="edit-justification-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJustificationDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmEdit} data-testid="confirm-edit-btn">
              Confirmar Alteração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Eliminar Registo
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acção irá desactivar o registo &quot;{selectedItem?.name}&quot;. O registo não será eliminado permanentemente e pode ser restaurado pelo administrador.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="delete-reason">Motivo da eliminação</Label>
            <Textarea
              id="delete-reason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Motivo da eliminação (mínimo 5 caracteres)..."
              className="mt-2"
              data-testid="delete-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Sales History Dialog */}
      <Dialog open={salesHistoryDialogOpen} onOpenChange={setSalesHistoryDialogOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Histórico de Vendas - {salesHistoryData?.account?.name}</DialogTitle>
          </DialogHeader>
          {salesHistoryData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{salesHistoryData.sales_count}</div>
                    <p className="text-sm text-muted-foreground">Vendas</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">
                      {new Intl.NumberFormat('pt-MZ', { style: 'currency', currency: 'MZN' }).format(salesHistoryData.total_received)}
                    </div>
                    <p className="text-sm text-muted-foreground">Total Recebido</p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="max-h-[300px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Venda</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Valor Pago</TableHead>
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesHistoryData.recent_sales?.map((sale) => {
                      const payment = sale.payments?.find(p => p.account_id === salesHistoryData.account.id);
                      return (
                        <TableRow key={sale.id}>
                          <TableCell className="font-mono">{formatDisplayCode(sale.sale_number, 'SAL')}</TableCell>
                          <TableCell>{formatDateTime(sale.created_at)}</TableCell>
                          <TableCell>
                            {new Intl.NumberFormat('pt-MZ', { style: 'currency', currency: 'MZN' }).format(payment?.amount || 0)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={sale.status === 'completed' ? 'default' : 'secondary'}>
                              {sale.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Stock Summary Dialog */}
      <Dialog open={stockSummaryDialogOpen} onOpenChange={setStockSummaryDialogOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Resumo de Stock - {stockSummaryData?.location?.name}</DialogTitle>
          </DialogHeader>
          {stockSummaryData && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{stockSummaryData.total_items}</div>
                    <p className="text-sm text-muted-foreground">Total Items</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-green-600">{stockSummaryData.in_stock}</div>
                    <p className="text-sm text-muted-foreground">Em Stock</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-blue-600">{stockSummaryData.sold}</div>
                    <p className="text-sm text-muted-foreground">Vendidos</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-orange-600">{stockSummaryData.repair}</div>
                    <p className="text-sm text-muted-foreground">Reparação</p>
                  </CardContent>
                </Card>
              </div>
              
              {stockSummaryData.products?.length > 0 && (
                <div className="max-h-[300px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead className="text-right">Quantidade</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stockSummaryData.products.map((product) => (
                        <TableRow key={product.product_id}>
                          <TableCell className="font-medium">{product.product_name}</TableCell>
                          <TableCell className="text-muted-foreground font-mono">{product.product_sku}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline">{product.quantity}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper functions
function getTabTitle(tab) {
  const titles = {
    'locations': 'Localizações',
    'payments-hub': 'Centro de Pagamentos',
    'payment-methods': 'Formas de Pagamento',
    'payment-accounts': 'Contas de Pagamento',
    'payment-conditions': 'Condições de Pagamento',
    'due-date-options': 'Vencimento do Documento',
    'warranty-options': 'Tempo de Garantia',
    'audit': 'Histórico de Auditoria',
    'rbac-coverage': 'Cobertura RBAC por Endpoint',
    'document-prefixes': 'Prefixos dos Documentos',
    'print-settings': 'Configurações de Impressão',
    'vat': 'Configuração de IVA',
    'service-qa': 'QA / Demonstração de Serviços',
    'data-migration': 'Migração da Base Activa',
  };
  return titles[tab] || '';
}

function getTabDescription(tab) {
  const descriptions = {
    'locations': 'Gerir lojas, armazéns e locais de stock',
    'payments-hub': 'Gerir formas de pagamento e contas no mesmo painel, mantendo a ligação entre ambos',
    'payment-methods': 'Gerir tipos de pagamento aceites (Numerário, POS, Transferência, etc.)',
    'payment-accounts': 'Gerir contas onde os pagamentos são recebidos',
    'payment-conditions': 'Gerir condições de pagamento (Pronto, Prestações, etc.)',
    'due-date-options': 'Gerir opções de vencimento de documentos',
    'warranty-options': 'Gerir períodos de garantia disponíveis',
    'audit': 'Visualizar histórico de todas as alterações',
    'rbac-coverage': 'Auditar rotas com RBAC explícito, fallback legado e endpoints públicos em tempo real',
    'document-prefixes': 'Definir como cada documento deve ser numerado (ex.: FP-000001, FA-000001, RE-000001)',
    'print-settings': 'Configurar impressão automática, templates por localização',
    'vat': 'Configurar taxa e regime de IVA padrão do sistema (Moçambique: 16%)',
    'service-qa': 'Gerar cenários reais de Serviços/Reparações e guardar histórico QA para repetição manual',
    'data-migration': 'Exportar e importar pacotes da base activa do sistema com UI simplificada para arquitectura de base única',
  };
  return descriptions[tab] || '';
}

// =============================================================================
// TAB COMPONENTS
// =============================================================================

function LocationsTab({ data, onEdit, onDelete, onRestore, onViewStock, isAdmin }) {
  return (
    <div className="space-y-4" data-testid="locations-tab-content">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Endereço</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acções</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                Nenhuma localização encontrada
              </TableCell>
            </TableRow>
          ) : (
            data.map((item) => {
              const typeInfo = LOCATION_TYPES.find(t => t.value === item.type);
              const TypeIcon = typeInfo?.icon || MapPin;
              return (
                <TableRow key={item.id} className={cn(item.is_deleted && "opacity-50 bg-destructive/5")}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <TypeIcon className="h-4 w-4 text-muted-foreground" />
                      {item.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {typeInfo?.label || item.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{item.address || '-'}</TableCell>
                  <TableCell>
                    {item.is_deleted ? (
                      <Badge variant="destructive">Eliminado</Badge>
                    ) : item.is_active ? (
                      <Badge variant="default">Activo</Badge>
                    ) : (
                      <Badge variant="secondary">Inactivo</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {item.is_deleted ? (
                      <Button size="sm" variant="outline" onClick={() => onRestore(item)} data-testid={`restore-${item.id}`}>
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => onViewStock(item)} data-testid={`stock-${item.id}`}>
                          <Package className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onEdit(item)} data-testid={`edit-${item.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => onDelete(item)} data-testid={`delete-${item.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function PaymentMethodsTab({ data, onEdit, onDelete, onRestore, isAdmin }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nome</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead>Notas</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="text-right">Acções</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
              Nenhuma forma de pagamento encontrada
            </TableCell>
          </TableRow>
        ) : (
          data.map((item) => (
            <TableRow key={item.id} className={cn(item.is_deleted && "opacity-50 bg-destructive/5")}>
              <TableCell className="font-medium">{item.name}</TableCell>
              <TableCell>
                <Badge variant="outline">
                  {PAYMENT_METHOD_TYPES.find(t => t.value === item.type)?.label || item.type}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">{item.notes || '-'}</TableCell>
              <TableCell>
                {item.is_deleted ? (
                  <Badge variant="destructive">Eliminado</Badge>
                ) : item.is_active ? (
                  <Badge variant="default">Activo</Badge>
                ) : (
                  <Badge variant="secondary">Inactivo</Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                {item.is_deleted ? (
                  <Button size="sm" variant="outline" onClick={() => onRestore(item)} data-testid={`restore-${item.id}`}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                ) : (
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => onEdit(item)} data-testid={`edit-${item.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => onDelete(item)} data-testid={`delete-${item.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function PaymentAccountsTab({ data, paymentMethods, onEdit, onDelete, onRestore, onViewHistory, isAdmin }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nome</TableHead>
          <TableHead>Forma de Pagamento</TableHead>
          <TableHead>Proprietário</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="text-right">Acções</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
              Nenhuma conta de pagamento encontrada
            </TableCell>
          </TableRow>
        ) : (
          data.map((item) => (
            <TableRow key={item.id} className={cn(item.is_deleted && "opacity-50 bg-destructive/5")}>
              <TableCell className="font-medium">{item.name}</TableCell>
              <TableCell>
                <Badge variant="outline">{item.payment_method_name || '-'}</Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {item.owner === 'CG Business' ? (
                    <Building className="h-4 w-4 text-blue-500" />
                  ) : (
                    <User className="h-4 w-4 text-green-500" />
                  )}
                  <span>{item.owner}</span>
                </div>
              </TableCell>
              <TableCell>
                {item.is_deleted ? (
                  <Badge variant="destructive">Eliminado</Badge>
                ) : item.is_active ? (
                  <Badge variant="default">Activo</Badge>
                ) : (
                  <Badge variant="secondary">Inactivo</Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                {item.is_deleted ? (
                  <Button size="sm" variant="outline" onClick={() => onRestore(item)} data-testid={`restore-${item.id}`}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                ) : (
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => onViewHistory(item)} data-testid={`history-${item.id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onEdit(item)} data-testid={`edit-${item.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => onDelete(item)} data-testid={`delete-${item.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function PaymentConditionsTab({ data, onEdit, onDelete, onRestore, isAdmin }) {
  const activeCount = data.filter((item) => !item.is_deleted && item.is_active).length;
  const inactiveCount = data.filter((item) => !item.is_deleted && !item.is_active).length;
  const withDescriptionCount = data.filter((item) => !item.is_deleted && item.description).length;

  return (
    <div className="space-y-4" data-testid="payment-conditions-tab-content">
      <div className="grid gap-3 md:grid-cols-3" data-testid="payment-conditions-summary-grid">
        <Card data-testid="payment-conditions-summary-active">
          <CardContent className="pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Activas</p>
            <p className="mt-2 text-2xl font-semibold">{activeCount}</p>
          </CardContent>
        </Card>
        <Card data-testid="payment-conditions-summary-inactive">
          <CardContent className="pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Inactivas</p>
            <p className="mt-2 text-2xl font-semibold">{inactiveCount}</p>
          </CardContent>
        </Card>
        <Card data-testid="payment-conditions-summary-described">
          <CardContent className="pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Com descrição</p>
            <p className="mt-2 text-2xl font-semibold">{withDescriptionCount}</p>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="payment-conditions-ux-note">
        <strong>Nota:</strong> Condição define a forma de cobrança (ex.: pronto, prestações) e é usada em conjunto com o vencimento do documento.
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Descrição</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acções</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                Nenhuma condição de pagamento encontrada
              </TableCell>
            </TableRow>
          ) : (
            data.map((item) => (
              <TableRow key={item.id} className={cn(item.is_deleted && "opacity-50 bg-destructive/5")}>
                <TableCell className="font-medium">{item.name}</TableCell>
                <TableCell className="text-muted-foreground">{item.description || '-'}</TableCell>
                <TableCell>
                  {item.is_deleted ? (
                    <Badge variant="destructive">Eliminado</Badge>
                  ) : item.is_active ? (
                    <Badge variant="default">Activo</Badge>
                  ) : (
                    <Badge variant="secondary">Inactivo</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {item.is_deleted ? (
                    <Button size="sm" variant="outline" onClick={() => onRestore(item)} data-testid={`restore-${item.id}`}>
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  ) : (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => onEdit(item)} data-testid={`edit-${item.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => onDelete(item)} data-testid={`delete-${item.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function DueDateOptionsTab({ data, onEdit, onDelete, onRestore, isAdmin }) {
  const activeCount = data.filter((item) => !item.is_deleted && item.is_active).length;
  const naCount = data.filter((item) => !item.is_deleted && (item.days === null || item.days === undefined)).length;
  const immediateCount = data.filter(
    (item) => !item.is_deleted && item.days !== null && item.days !== undefined && Number(item.days) === 0,
  ).length;

  const getDueDateLabel = (days) => {
    if (days === null || days === undefined || days === '') return 'N/A';
    if (Number(days) === 0) return 'No ato';
    return `${days} dias`;
  };

  const getDueDateProfile = (days) => {
    if (days === null || days === undefined || days === '') {
      return { label: 'Sem prazo', variant: 'secondary' };
    }
    const numericDays = Number(days);
    if (numericDays <= 0) return { label: 'Imediato', variant: 'default' };
    if (numericDays <= 15) return { label: 'Curto prazo', variant: 'outline' };
    if (numericDays <= 60) return { label: 'Médio prazo', variant: 'secondary' };
    return { label: 'Longo prazo', variant: 'secondary' };
  };

  return (
    <div className="space-y-4" data-testid="due-date-options-tab-content">
      <div className="grid gap-3 md:grid-cols-3" data-testid="due-date-options-summary-grid">
        <Card data-testid="due-date-options-summary-active">
          <CardContent className="pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Activos</p>
            <p className="mt-2 text-2xl font-semibold">{activeCount}</p>
          </CardContent>
        </Card>
        <Card data-testid="due-date-options-summary-na">
          <CardContent className="pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Sem prazo (N/A)</p>
            <p className="mt-2 text-2xl font-semibold">{naCount}</p>
          </CardContent>
        </Card>
        <Card data-testid="due-date-options-summary-immediate">
          <CardContent className="pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">No ato</p>
            <p className="mt-2 text-2xl font-semibold">{immediateCount}</p>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="due-date-options-ux-note">
        <strong>Nota:</strong> O vencimento define prazo documental e pode combinar com qualquer condição de pagamento.
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Dias</TableHead>
            <TableHead>Perfil</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acções</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                Nenhuma opção de vencimento encontrada
              </TableCell>
            </TableRow>
          ) : (
            data.map((item) => {
              const profile = getDueDateProfile(item.days);
              return (
                <TableRow key={item.id} className={cn(item.is_deleted && "opacity-50 bg-destructive/5")}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{getDueDateLabel(item.days)}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={profile.variant}>{profile.label}</Badge>
                  </TableCell>
                  <TableCell>
                    {item.is_deleted ? (
                      <Badge variant="destructive">Eliminado</Badge>
                    ) : item.is_active ? (
                      <Badge variant="default">Activo</Badge>
                    ) : (
                      <Badge variant="secondary">Inactivo</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {item.is_deleted ? (
                      <Button size="sm" variant="outline" onClick={() => onRestore(item)} data-testid={`restore-${item.id}`}>
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => onEdit(item)} data-testid={`edit-${item.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => onDelete(item)} data-testid={`delete-${item.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function WarrantyOptionsTab({ data, onEdit, onDelete, onRestore, isAdmin }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nome</TableHead>
          <TableHead>Dias</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="text-right">Acções</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
              Nenhuma opção de garantia encontrada
            </TableCell>
          </TableRow>
        ) : (
          data.map((item) => (
            <TableRow key={item.id} className={cn(item.is_deleted && "opacity-50 bg-destructive/5")}>
              <TableCell className="font-medium">{item.name}</TableCell>
              <TableCell>
                {item.days !== null ? (
                  <Badge variant="outline">{item.days} dias</Badge>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                {item.is_deleted ? (
                  <Badge variant="destructive">Eliminado</Badge>
                ) : item.is_active ? (
                  <Badge variant="default">Activo</Badge>
                ) : (
                  <Badge variant="secondary">Inactivo</Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                {item.is_deleted ? (
                  <Button size="sm" variant="outline" onClick={() => onRestore(item)} data-testid={`restore-${item.id}`}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                ) : (
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => onEdit(item)} data-testid={`edit-${item.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => onDelete(item)} data-testid={`delete-${item.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function AuditLogsTab({ data }) {
  const getActionBadge = (action) => {
    const variants = {
      created: 'default',
      edited: 'secondary',
      deleted: 'destructive',
      restored: 'outline',
      seeded: 'default',
    };
    const labels = {
      created: 'Criado',
      edited: 'Editado',
      deleted: 'Eliminado',
      restored: 'Restaurado',
      seeded: 'Seed',
      migrated: 'Migrado',
    };
    return <Badge variant={variants[action] || 'secondary'}>{labels[action] || action}</Badge>;
  };
  
  const getEntityTypeBadge = (type) => {
    const labels = {
      location: 'Localização',
      payment_method: 'Forma Pgto',
      payment_account: 'Conta',
      employee: 'Colaborador',
      payment_condition: 'Condição',
      due_date_option: 'Vencimento',
      warranty_option: 'Garantia',
      system: 'Sistema',
    };
    return <Badge variant="outline">{labels[type] || type}</Badge>;
  };
  
  return (
    <div className="max-h-[500px] overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data/Hora</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Entidade</TableHead>
            <TableHead>Acção</TableHead>
            <TableHead>Utilizador</TableHead>
            <TableHead>Detalhes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                Nenhum registo de auditoria encontrado
              </TableCell>
            </TableRow>
          ) : (
            data.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="text-sm">{formatDateTime(log.timestamp)}</TableCell>
                <TableCell>{getEntityTypeBadge(log.entity_type)}</TableCell>
                <TableCell className="font-medium">{log.entity_name}</TableCell>
                <TableCell>{getActionBadge(log.action)}</TableCell>
                <TableCell className="text-muted-foreground">{log.user_name}</TableCell>
                <TableCell className="max-w-[200px] truncate text-muted-foreground">
                  {log.justification || log.delete_reason || log.details || '-'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// =============================================================================
// FORM COMPONENTS
// =============================================================================

function LocationForm({ formData, setFormData, editMode }) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="loc-name">Nome *</Label>
        <Input
          id="loc-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ex: Novare Matola, Armazém Central..."
          data-testid="loc-name-input"
        />
      </div>
      <div>
        <Label htmlFor="loc-type">Tipo *</Label>
        <Select
          value={formData.type}
          onValueChange={(value) => setFormData({ ...formData, type: value })}
        >
          <SelectTrigger id="loc-type" data-testid="loc-type-select">
            <SelectValue placeholder="Seleccione o tipo" />
          </SelectTrigger>
          <SelectContent>
            {LOCATION_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="loc-address">Endereço</Label>
        <Input
          id="loc-address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="Endereço ou descrição..."
          data-testid="loc-address-input"
        />
      </div>
      <div>
        <Label htmlFor="loc-notes">Notas</Label>
        <Textarea
          id="loc-notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Notas adicionais..."
          data-testid="loc-notes-input"
        />
      </div>
      {editMode && (
        <div className="flex items-center space-x-2">
          <Switch
            id="loc-is-active"
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            data-testid="loc-is-active-switch"
          />
          <Label htmlFor="loc-is-active">Activo</Label>
        </div>
      )}
    </div>
  );
}

function PaymentMethodForm({ formData, setFormData, editMode }) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="pm-name">Nome *</Label>
        <Input
          id="pm-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ex: Numerário, POS BCI..."
          data-testid="pm-name-input"
        />
      </div>
      <div>
        <Label htmlFor="pm-type">Tipo *</Label>
        <Select
          value={formData.type}
          onValueChange={(value) => setFormData({ ...formData, type: value })}
        >
          <SelectTrigger id="pm-type" data-testid="pm-type-select">
            <SelectValue placeholder="Seleccione o tipo" />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_METHOD_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="pm-notes">Notas</Label>
        <Textarea
          id="pm-notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Notas adicionais..."
          data-testid="pm-notes-input"
        />
      </div>
      {editMode && (
        <div className="flex items-center space-x-2">
          <Switch
            id="pm-is-active"
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            data-testid="pm-is-active-switch"
          />
          <Label htmlFor="pm-is-active">Activo</Label>
        </div>
      )}
    </div>
  );
}

function PaymentAccountForm({ formData, setFormData, paymentMethods }) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="pa-name">Nome *</Label>
        <Input
          id="pa-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ex: CAIXA, BCI CGB..."
          data-testid="pa-name-input"
        />
      </div>
      <div>
        <Label htmlFor="pa-method">Forma de Pagamento *</Label>
        <Select
          value={formData.payment_method_id}
          onValueChange={(value) => setFormData({ ...formData, payment_method_id: value })}
        >
          <SelectTrigger id="pa-method" data-testid="pa-method-select">
            <SelectValue placeholder="Seleccione a forma de pagamento" />
          </SelectTrigger>
          <SelectContent>
            {paymentMethods.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                {method.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="pa-owner">Proprietário *</Label>
        <Select
          value={formData.owner}
          onValueChange={(value) => setFormData({ ...formData, owner: value })}
        >
          <SelectTrigger id="pa-owner" data-testid="pa-owner-select">
            <SelectValue placeholder="Seleccione o proprietário" />
          </SelectTrigger>
          <SelectContent>
            {ACCOUNT_OWNERS.map((owner) => (
              <SelectItem key={owner.value} value={owner.value}>
                {owner.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="pa-notes">Notas</Label>
        <Textarea
          id="pa-notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Notas adicionais..."
          data-testid="pa-notes-input"
        />
      </div>
    </div>
  );
}

function PaymentConditionForm({ formData, setFormData, editMode }) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="pc-name">Nome *</Label>
        <Input
          id="pc-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ex: Pronto Pagamento, Prestações..."
          data-testid="pc-name-input"
        />
      </div>
      <div>
        <Label htmlFor="pc-description">Descrição</Label>
        <Textarea
          id="pc-description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Descrição da condição de pagamento..."
          data-testid="pc-description-input"
        />
      </div>
      {editMode && (
        <div className="flex items-center space-x-2">
          <Switch
            id="pc-is-active"
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            data-testid="pc-is-active-switch"
          />
          <Label htmlFor="pc-is-active">Activo</Label>
        </div>
      )}
    </div>
  );
}

function DueDateOptionForm({ formData, setFormData, editMode }) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="dd-name">Nome *</Label>
        <Input
          id="dd-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ex: 7 Dias, 15 Dias, 1 Mês..."
          data-testid="dd-name-input"
        />
      </div>
      <div>
        <Label htmlFor="dd-days">Número de Dias</Label>
        <Input
          id="dd-days"
          type="number"
          min="0"
          value={formData.days}
          onChange={(e) => setFormData({ ...formData, days: e.target.value })}
          placeholder="Deixe vazio para N/A"
          data-testid="dd-days-input"
        />
        <p className="text-sm text-muted-foreground mt-1">
          Deixe vazio se não aplicável (N/A)
        </p>
      </div>
      {editMode && (
        <div className="flex items-center space-x-2">
          <Switch
            id="dd-is-active"
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            data-testid="dd-is-active-switch"
          />
          <Label htmlFor="dd-is-active">Activo</Label>
        </div>
      )}
    </div>
  );
}

function WarrantyOptionForm({ formData, setFormData, editMode }) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="wo-name">Nome *</Label>
        <Input
          id="wo-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ex: 3 Meses, 6 Meses, 1 Ano..."
          data-testid="wo-name-input"
        />
      </div>
      <div>
        <Label htmlFor="wo-days">Duração em Dias</Label>
        <Input
          id="wo-days"
          type="number"
          min="0"
          value={formData.days}
          onChange={(e) => setFormData({ ...formData, days: e.target.value })}
          placeholder="Deixe vazio para N/A"
          data-testid="wo-days-input"
        />
        <p className="text-sm text-muted-foreground mt-1">
          Deixe vazio se não aplicável (N/A)
        </p>
      </div>
      {editMode && (
        <div className="flex items-center space-x-2">
          <Switch
            id="wo-is-active"
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            data-testid="wo-is-active-switch"
          />
          <Label htmlFor="wo-is-active">Activo</Label>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// PRINT SETTINGS TAB - Configurações de Impressão
// =============================================================================
const PRINT_SETTINGS_CACHE_TTL_MS = 30000;
let printSettingsDataCache = {
  payload: null,
  expiresAt: 0,
  inflight: null,
};

const clonePrintSettingsPayload = (payload) => {
  if (typeof structuredClone === 'function') {
    return structuredClone(payload);
  }

  return JSON.parse(JSON.stringify(payload));
};

const getCachedPrintSettingsPayload = () => {
  if (!printSettingsDataCache.payload || printSettingsDataCache.expiresAt < Date.now()) {
    return null;
  }

  return clonePrintSettingsPayload(printSettingsDataCache.payload);
};

const setCachedPrintSettingsPayload = (payload) => {
  printSettingsDataCache = {
    ...printSettingsDataCache,
    payload: clonePrintSettingsPayload(payload),
    expiresAt: Date.now() + PRINT_SETTINGS_CACHE_TTL_MS,
  };
};

const clearCachedPrintSettingsPayload = () => {
  printSettingsDataCache = {
    payload: null,
    expiresAt: 0,
    inflight: null,
  };
};

function PrintSettingsTab({ locations }) {
  const navigate = useNavigate();
  const storageKey = 'cg-business-print-cockpit-v1';
  const normalizePrintTab = useCallback((value) => ({
    globals: 'presets',
    banking: 'presets',
    locations: 'presets',
  }[value] || value || 'presets'), []);
  const savedState = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey) || '{}');
    } catch {
      return {};
    }
  })();

  const defaultBuilderFilters = {
    location_id: 'ALL',
    doc_type: 'ALL',
    module_group: 'ALL',
    format: 'ALL',
    status: 'ALL',
    search: '',
  };

  const moduleOptions = useMemo(() => [
    { value: 'VENDAS', label: 'Vendas', badgeClass: 'bg-blue-100 text-blue-800 border-blue-200' },
    { value: 'DOCUMENTOS', label: 'Documentos', badgeClass: 'bg-rose-100 text-rose-800 border-rose-200' },
    { value: 'SERVIÇOS', label: 'Serviços', badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    { value: 'CONTAS_A_RECEBER', label: 'AR', badgeClass: 'bg-amber-100 text-amber-800 border-amber-200' },
    { value: 'COMPRAS', label: 'Compras', badgeClass: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
    { value: 'STOCK', label: 'Stock', badgeClass: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
    { value: 'GAMING', label: 'Gaming', badgeClass: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200' },
    { value: 'CAIXA', label: 'Caixa', badgeClass: 'bg-orange-100 text-orange-800 border-orange-200' },
    { value: 'OUTROS', label: 'Outros', badgeClass: 'bg-slate-100 text-slate-700 border-slate-200' },
  ], []);

  const moduleAliasMap = useMemo(() => ({
    VENDAS: 'VENDAS',
    DOCUMENTOS: 'DOCUMENTOS',
    COTACOES: 'DOCUMENTOS',
    COTAÇÕES: 'DOCUMENTOS',
    SERVICOS: 'SERVIÇOS',
    SERVIÇOS: 'SERVIÇOS',
    AR: 'CONTAS_A_RECEBER',
    CONTAS_A_RECEBER: 'CONTAS_A_RECEBER',
    COMPRAS: 'COMPRAS',
    STOCK: 'STOCK',
    GAMING: 'GAMING',
    CAIXA: 'CAIXA',
    OUTRO: 'OUTROS',
    OUTROS: 'OUTROS',
  }), []);

  const moduleOrder = useMemo(() => moduleOptions.reduce((acc, item, index) => {
    acc[item.value] = index;
    return acc;
  }, {}), [moduleOptions]);

  const [globalSettings, setGlobalSettings] = useState({
    auto_print_pos: true,
    print_copies: 2,
    pos_width: '80mm',
    show_signature_line: true,
    show_qr_code: false,
    footer_text: 'Obrigado pela sua preferência! Política de trocas: 7 dias com factura.',
  });
  const [printSettings, setPrintSettings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateDiagnostics, setTemplateDiagnostics] = useState({ summary: {}, duplicate_scopes: [], location_legacy_scopes: [], print_preset_warnings: [] });
  const [auditLogs, setAuditLogs] = useState([]);
  const [companyBanking, setCompanyBanking] = useState(EMPTY_PRINT_BANKING_FORM);
  const [locationBankingList, setLocationBankingList] = useState([]);
  const [selectedBankingLocationId, setSelectedBankingLocationId] = useState('');
  const [locationBankingForm, setLocationBankingForm] = useState(EMPTY_PRINT_BANKING_FORM);
  const [activePrintTab, setActivePrintTab] = useState(normalizePrintTab(savedState.activePrintTab || 'presets'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBanking, setSavingBanking] = useState('');
  const [dirtyGlobal, setDirtyGlobal] = useState(false);
  const [dirtyCompanyBanking, setDirtyCompanyBanking] = useState(false);
  const [dirtyLocationBanking, setDirtyLocationBanking] = useState(false);
  const [selectedLocationPresetId, setSelectedLocationPresetId] = useState('');
  const [locationPresetForm, setLocationPresetForm] = useState({
    auto_print_pos: true,
    print_copies: 2,
    pos_width: '80mm',
    show_signature_line: true,
    show_qr_code: false,
    footer_text: 'Obrigado pela sua preferência! Política de trocas: 7 dias com factura.',
  });
  const [dirtyLocationPreset, setDirtyLocationPreset] = useState(false);
  const [savingLocationPreset, setSavingLocationPreset] = useState(false);
  const [printCompareData, setPrintCompareData] = useState(null);
  const [printCompareLoading, setPrintCompareLoading] = useState(false);
  const [builderFilters, setBuilderFilters] = useState({
    ...defaultBuilderFilters,
    ...(savedState.builderFilters || {}),
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState(savedState.selectedTemplateId || null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [effectivePreviewUrl, setEffectivePreviewUrl] = useState('');
  const [templateCompareData, setTemplateCompareData] = useState(null);
  const [templateCompareLoading, setTemplateCompareLoading] = useState(false);
  const [templateAuditLogs, setTemplateAuditLogs] = useState([]);
  const [templateAuditLoading, setTemplateAuditLoading] = useState(false);
  const [templatePreviewMode, setTemplatePreviewMode] = useState('sample');
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [auditFilters, setAuditFilters] = useState({ dateFrom: '', dateTo: '', location_id: 'ALL', doc_type: 'ALL', module_group: 'ALL' });
  const builderCardRef = useRef(null);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();

  const posDocTypes = useMemo(() => ['pos_receipt', 'pos_gaming'], []);

  const applyPrintSettingsPayload = useCallback((payload) => {
    const settingsList = Array.isArray(payload?.settingsList) ? payload.settingsList : [];
    const global = settingsList.find((item) => item.is_global);
    if (global) setGlobalSettings(global);
    setPrintSettings(settingsList.filter((item) => !item.is_global));
    setTemplates(payload?.templates || []);
    const bankingPayload = payload?.banking || {};
    setCompanyBanking({ ...EMPTY_PRINT_BANKING_FORM, ...(bankingPayload.company || {}) });
    const nextLocations = Array.isArray(bankingPayload.locations) ? bankingPayload.locations : [];
    setLocationBankingList(nextLocations);
    setSelectedBankingLocationId((prev) => prev || nextLocations[0]?.id || '');
  }, []);

  const normalizeModuleValue = useCallback((value) => {
    const raw = String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase()
      .replace(/[\s/-]+/g, '_')
      .replace(/_+/g, '_');
    return moduleAliasMap[raw] || 'OUTROS';
  }, [moduleAliasMap]);

  const getModuleMeta = useCallback((value) => moduleOptions.find((item) => item.value === normalizeModuleValue(value)) || moduleOptions[moduleOptions.length - 1], [moduleOptions, normalizeModuleValue]);

  const getBranchLabel = (locationName) => {
    const lower = String(locationName || '').toLowerCase();
    if (!lower || lower === 'global') return 'GLOBAL';
    if (lower.includes('maputo') || lower.includes('central') || lower.includes('sede')) return 'SEDE';
    if (lower.includes('matola') || lower.includes('sucursal') || lower.includes('novare')) return 'SUCURSAL';
    return 'FILIAL';
  };

  const getBranchOrder = (branchLabel) => {
    if (branchLabel === 'SEDE') return 0;
    if (branchLabel === 'SUCURSAL') return 1;
    if (branchLabel === 'FILIAL') return 2;
    return 3;
  };

  const loadAll = useCallback(async (forceReload = false) => {
    let requestPromise = null;
    setLoading(true);
    try {
      if (!forceReload) {
        const cachedPayload = getCachedPrintSettingsPayload();
        if (cachedPayload) {
          applyPrintSettingsPayload(cachedPayload);
          setTemplateDiagnostics(cachedPayload.diagnostics || { summary: {}, duplicate_scopes: [], location_legacy_scopes: [], print_preset_warnings: [] });
          return;
        }

        if (printSettingsDataCache.inflight) {
          const inflightPayload = await printSettingsDataCache.inflight;
          applyPrintSettingsPayload(inflightPayload);
          setTemplateDiagnostics(inflightPayload.diagnostics || { summary: {}, duplicate_scopes: [], location_legacy_scopes: [], print_preset_warnings: [] });
          return;
        }
      }

      resetFetchTelemetry();
      requestPromise = Promise.all([
        safeRequest(
          () => fetchJsonWithAuth('/api/print/settings/all', {}, 'Erro ao carregar políticas de impressão'),
          [],
          {
            timeoutMs: 30000,
            retries: 1,
            required: true,
            telemetry: { module: 'print-settings', endpoint: 'print.settings.all', onEvent: captureFetchTelemetry },
          },
        ),
        safeRequest(
          () => fetchJsonWithAuth('/api/templates/', {}, 'Erro ao carregar templates de impressão'),
          { templates: [] },
          {
            timeoutMs: 30000,
            retries: 1,
            required: true,
            telemetry: { module: 'print-settings', endpoint: 'templates.list', onEvent: captureFetchTelemetry },
          },
        ),
        safeRequest(
          () => fetchJsonWithAuth('/api/print/settings/banking', {}, 'Erro ao carregar dados bancários de impressão'),
          { company: EMPTY_PRINT_BANKING_FORM, locations: [] },
          {
            timeoutMs: 30000,
            retries: 1,
            required: true,
            telemetry: { module: 'print-settings', endpoint: 'print.settings.banking', onEvent: captureFetchTelemetry },
          },
        ),
        safeRequest(
          () => fetchJsonWithAuth('/api/templates/cockpit-diagnostics', {}, 'Erro ao carregar diagnóstico do cockpit'),
          { summary: {}, duplicate_scopes: [], location_legacy_scopes: [], print_preset_warnings: [] },
          {
            timeoutMs: 30000,
            retries: 1,
            required: true,
            telemetry: { module: 'print-settings', endpoint: 'templates.cockpit-diagnostics', onEvent: captureFetchTelemetry },
          },
        ),
      ]).then(([settingsData, templatesData, bankingData, diagnosticsData]) => ({
        settingsList: Array.isArray(settingsData) ? settingsData : [],
        templates: templatesData.templates || [],
        banking: bankingData || { company: EMPTY_PRINT_BANKING_FORM, locations: [] },
        diagnostics: diagnosticsData || { summary: {}, duplicate_scopes: [], location_legacy_scopes: [], print_preset_warnings: [] },
      }));

      printSettingsDataCache = {
        ...printSettingsDataCache,
        inflight: requestPromise,
      };

      const payload = await requestPromise;
      setCachedPrintSettingsPayload(payload);
      applyPrintSettingsPayload(payload);
      setTemplateDiagnostics(payload.diagnostics || { summary: {}, duplicate_scopes: [], location_legacy_scopes: [], print_preset_warnings: [] });
    } catch (error) {
      console.error('Error loading print cockpit:', error);
      toast.error(getErrorMessage(error, 'Erro ao carregar cockpit de impressão'));
    } finally {
      if (requestPromise && printSettingsDataCache.inflight === requestPromise) {
        printSettingsDataCache = {
          ...printSettingsDataCache,
          inflight: null,
        };
      }
      setLoading(false);
    }
  }, [applyPrintSettingsPayload, captureFetchTelemetry, resetFetchTelemetry]);

  useEffect(() => {
    if (!locationBankingList.length) {
      setLocationBankingForm(EMPTY_PRINT_BANKING_FORM);
      return;
    }
    const selectedLocation = locationBankingList.find((item) => item.id === selectedBankingLocationId) || locationBankingList[0];
    if (!selectedLocation) {
      setLocationBankingForm(EMPTY_PRINT_BANKING_FORM);
      return;
    }
    if (selectedLocation.id !== selectedBankingLocationId) {
      setSelectedBankingLocationId(selectedLocation.id);
    }
    setLocationBankingForm({
      bank_account_holder: selectedLocation.bank_account_holder || '',
      bank_name: selectedLocation.bank_name || '',
      bank_account_number: selectedLocation.bank_account_number || '',
      bank_iban: selectedLocation.bank_iban || '',
      bank_nib: selectedLocation.bank_nib || '',
      bank_swift: selectedLocation.bank_swift || '',
      additional_banks: selectedLocation.additional_banks || [],
    });
    setDirtyLocationBanking(false);
  }, [locationBankingList, selectedBankingLocationId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const runAuditSearch = useCallback(async () => {
    const params = new URLSearchParams();
    if (auditFilters.dateFrom) params.set('date_from', auditFilters.dateFrom);
    if (auditFilters.dateTo) params.set('date_to', auditFilters.dateTo);
    if (auditFilters.location_id !== 'ALL') params.set('location_id', auditFilters.location_id);
    if (auditFilters.doc_type !== 'ALL') params.set('doc_type', auditFilters.doc_type);
    if (auditFilters.module_group !== 'ALL') params.set('module_group', auditFilters.module_group);
    try {
      const query = params.toString();
      const data = await fetchJsonWithAuth(`/api/templates/audit/print${query ? `?${query}` : ''}`, {}, 'Erro ao filtrar auditoria de impressão');
      setAuditLogs(data.logs || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao filtrar auditoria'));
    }
  }, [auditFilters]);

  useEffect(() => {
    if (activePrintTab !== 'audit' || auditLogs.length > 0) return;
    runAuditSearch();
  }, [activePrintTab, auditLogs.length, runAuditSearch]);

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({
      activePrintTab,
      builderFilters,
      selectedTemplateId,
    }));
  }, [activePrintTab, builderFilters, selectedTemplateId]);

  const switchPrintTab = (nextTab) => {
    const normalizedNextTab = normalizePrintTab(nextTab);
    if (dirtyGlobal && normalizedNextTab !== activePrintTab) {
      const proceed = window.confirm('Existem alterações não guardadas em Globais. Deseja mudar de tab mesmo assim?');
      if (!proceed) return;
    }
    setActivePrintTab(normalizedNextTab);
  };

  const handleGlobalFieldChange = (patch) => {
    setDirtyGlobal(true);
    setGlobalSettings((prev) => ({ ...prev, ...patch }));
  };

  const handleCompanyBankingFieldChange = (patch) => {
    setDirtyCompanyBanking(true);
    setCompanyBanking((prev) => ({ ...prev, ...patch }));
  };

  const handleLocationBankingFieldChange = (patch) => {
    setDirtyLocationBanking(true);
    setLocationBankingForm((prev) => ({ ...prev, ...patch }));
  };

  const handleLocationPresetFieldChange = (patch) => {
    setDirtyLocationPreset(true);
    setLocationPresetForm((prev) => ({ ...prev, ...patch }));
  };

  const handleCompanyAdditionalBankChange = (index, patch) => {
    setDirtyCompanyBanking(true);
    setCompanyBanking((prev) => ({
      ...prev,
      additional_banks: (prev.additional_banks || []).map((bank, bankIndex) => (
        bankIndex === index ? { ...bank, ...patch } : bank
      )),
    }));
  };

  const handleLocationAdditionalBankChange = (index, patch) => {
    setDirtyLocationBanking(true);
    setLocationBankingForm((prev) => ({
      ...prev,
      additional_banks: (prev.additional_banks || []).map((bank, bankIndex) => (
        bankIndex === index ? { ...bank, ...patch } : bank
      )),
    }));
  };

  const addCompanyAdditionalBank = () => {
    setDirtyCompanyBanking(true);
    setCompanyBanking((prev) => ({
      ...prev,
      additional_banks: [...(prev.additional_banks || []), createEmptyAdditionalBank((prev.additional_banks || []).length)],
    }));
  };

  const addLocationAdditionalBank = () => {
    setDirtyLocationBanking(true);
    setLocationBankingForm((prev) => ({
      ...prev,
      additional_banks: [...(prev.additional_banks || []), createEmptyAdditionalBank((prev.additional_banks || []).length)],
    }));
  };

  const removeCompanyAdditionalBank = (index) => {
    setDirtyCompanyBanking(true);
    setCompanyBanking((prev) => ({
      ...prev,
      additional_banks: (prev.additional_banks || []).filter((_, bankIndex) => bankIndex !== index),
    }));
  };

  const removeLocationAdditionalBank = (index) => {
    setDirtyLocationBanking(true);
    setLocationBankingForm((prev) => ({
      ...prev,
      additional_banks: (prev.additional_banks || []).filter((_, bankIndex) => bankIndex !== index),
    }));
  };

  const handleSaveGlobalSettings = async () => {
    setSaving(true);
    try {
      await fetchJsonWithAuth('/api/print/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...globalSettings, is_global: true })
      }, 'Erro ao guardar configurações globais');
      setDirtyGlobal(false);
      toast.success('Configurações globais guardadas');
      clearCachedPrintSettingsPayload();
      loadAll(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar configurações globais'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCompanyBanking = async () => {
    setSavingBanking('company');
    try {
      await fetchJsonWithAuth('/api/print/settings/banking/company', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companyBanking),
      }, 'Erro ao guardar dados bancários globais');
      setDirtyCompanyBanking(false);
      toast.success('Dados bancários globais guardados');
      clearCachedPrintSettingsPayload();
      loadAll(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar dados bancários globais'));
    } finally {
      setSavingBanking('');
    }
  };

  const handleSaveLocationBanking = async () => {
    if (!selectedBankingLocationId) return;
    setSavingBanking('location');
    try {
      await fetchJsonWithAuth(`/api/print/settings/banking/location/${selectedBankingLocationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(locationBankingForm),
      }, 'Erro ao guardar dados bancários da localização');
      setDirtyLocationBanking(false);
      toast.success('Dados bancários da localização guardados');
      clearCachedPrintSettingsPayload();
      loadAll(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar dados bancários da localização'));
    } finally {
      setSavingBanking('');
    }
  };

  const handleClearLocationBanking = async () => {
    setLocationBankingForm(EMPTY_PRINT_BANKING_FORM);
    setDirtyLocationBanking(true);
    if (!selectedBankingLocationId) return;
    setSavingBanking('location');
    try {
      await fetchJsonWithAuth(`/api/print/settings/banking/location/${selectedBankingLocationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(EMPTY_PRINT_BANKING_FORM),
      }, 'Erro ao limpar dados bancários da localização');
      setDirtyLocationBanking(false);
      toast.success('Override bancário da localização removido');
      clearCachedPrintSettingsPayload();
      loadAll(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao limpar dados bancários da localização'));
    } finally {
      setSavingBanking('');
    }
  };

  const updateBuilderFilters = (patch) => {
    setBuilderFilters((prev) => ({ ...prev, ...patch }));
  };

  const jumpToBuilder = (patch = {}) => {
    switchPrintTab('builder');
    updateBuilderFilters(patch);
    setTimeout(() => {
      builderCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const resetBuilderFilters = () => {
    setBuilderFilters(defaultBuilderFilters);
  };

  const normalizedTemplates = useMemo(() => {
    return templates.map((template) => {
      const primaryModule = normalizeModuleValue(template.origin_context || template.primaryModule || (template.module_group || [])[0]);
      const moduleMeta = getModuleMeta(primaryModule);
      const locationName = template.location_name || locations.find((loc) => loc.id === template.location_id)?.name || 'Global';
      const branchLabel = getBranchLabel(locationName);
      const updatedValue = template.updated_at || template.created_at || '';
      const defaultBadge = template.is_default ? 'Padrão' : 'Não padrão';
      const activeBadge = template.is_deleted ? 'Apagado' : template.is_active === false ? 'Inactivo' : 'Activo';

      return {
        ...template,
        primaryModule,
        moduleMeta,
        usedInLabel: moduleMeta.label,
        locationName,
        branchLabel,
        updatedLabel: updatedValue ? formatDateTime(updatedValue) : 'Sem registo',
        defaultBadge,
        activeBadge,
        isPosTemplate: posDocTypes.includes(template.document_type),
        originContext: template.origin_context || primaryModule,
        documentFamily: template.document_family || template.document_type,
        isModuleLegacy: Boolean(template.is_module_legacy),
        isLocationLegacy: Boolean(template.is_location_legacy),
      };
    });
  }, [getModuleMeta, locations, normalizeModuleValue, posDocTypes, templates]);

  const uniqueDocTypes = useMemo(() => {
    return [...new Set(normalizedTemplates.map((item) => item.document_type).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt'));
  }, [normalizedTemplates]);

  const cockpitStats = useMemo(() => {
    const activeTemplates = normalizedTemplates.filter((item) => !item.is_deleted && item.is_active !== false);
    const defaultTemplates = activeTemplates.filter((item) => item.is_default);
    const coveredLocations = new Set(activeTemplates.filter((item) => item.location_id).map((item) => item.location_id));
    const coveredDocTypes = new Set(activeTemplates.map((item) => item.document_type));
    return {
      total: normalizedTemplates.length,
      active: activeTemplates.length,
      defaults: defaultTemplates.length,
      branches: coveredLocations.size,
      docTypes: coveredDocTypes.size,
    };
  }, [normalizedTemplates]);
  const diagnosticsSummary = templateDiagnostics?.summary || {};

  const locationCoverage = useMemo(() => {
    return locations.map((location) => {
      const scopedTemplates = normalizedTemplates.filter((item) => item.location_id === location.id && !item.is_deleted);
      const posTemplates = scopedTemplates.filter((item) => item.isPosTemplate);
      const a4Templates = scopedTemplates.filter((item) => !item.isPosTemplate);
      const defaults = scopedTemplates.filter((item) => item.is_default);
      return {
        ...location,
        scopedTemplates,
        posCount: posTemplates.length,
        a4Count: a4Templates.length,
        defaultCount: defaults.length,
      };
    });
  }, [locations, normalizedTemplates]);

  const locationPresetMap = useMemo(() => printSettings.reduce((acc, item) => {
    if (item.location_id) {
      acc[item.location_id] = item;
    }
    return acc;
  }, {}), [printSettings]);

  const selectedLocationPresetRecord = selectedLocationPresetId ? locationPresetMap[selectedLocationPresetId] || null : null;
  const selectedLocationPresetSummary = useMemo(() => ({
    ...globalSettings,
    ...(selectedLocationPresetRecord || {}),
  }), [globalSettings, selectedLocationPresetRecord]);

  useEffect(() => {
    const nextLocationId = selectedLocationPresetId || locationCoverage[0]?.id || '';
    if (nextLocationId && nextLocationId !== selectedLocationPresetId) {
      setSelectedLocationPresetId(nextLocationId);
      return;
    }
    if (!nextLocationId) return;
    setLocationPresetForm({
      auto_print_pos: selectedLocationPresetSummary.auto_print_pos ?? true,
      print_copies: selectedLocationPresetSummary.print_copies ?? 2,
      pos_width: selectedLocationPresetSummary.pos_width || '80mm',
      show_signature_line: selectedLocationPresetSummary.show_signature_line ?? true,
      show_qr_code: selectedLocationPresetSummary.show_qr_code ?? false,
      footer_text: selectedLocationPresetSummary.footer_text || '',
    });
    setDirtyLocationPreset(false);
  }, [locationCoverage, selectedLocationPresetId, selectedLocationPresetSummary]);

  useEffect(() => {
    let cancelled = false;

    const loadPrintCompare = async () => {
      setPrintCompareLoading(true);
      try {
        const query = selectedLocationPresetId ? `?location_id=${encodeURIComponent(selectedLocationPresetId)}` : '';
        const payload = await fetchJsonWithAuth(`/api/print/settings/compare${query}`, {}, 'Erro ao carregar comparador de presets');
        if (!cancelled) {
          setPrintCompareData(payload);
        }
      } catch (error) {
        if (!cancelled) {
          setPrintCompareData(null);
        }
        console.error('Erro ao carregar comparador de presets:', error);
      } finally {
        if (!cancelled) {
          setPrintCompareLoading(false);
        }
      }
    };

    loadPrintCompare();
    return () => {
      cancelled = true;
    };
  }, [selectedLocationPresetId]);

  const filteredTemplates = useMemo(() => {
    return [...normalizedTemplates]
      .filter((template) => {
        if (builderFilters.location_id !== 'ALL') {
          const templateLocation = template.location_id || 'GLOBAL';
          if (templateLocation !== builderFilters.location_id) return false;
        }
        if (builderFilters.doc_type !== 'ALL' && template.document_type !== builderFilters.doc_type) return false;
        if (builderFilters.module_group !== 'ALL' && template.primaryModule !== builderFilters.module_group) return false;
        if (builderFilters.format === 'POS' && !template.isPosTemplate) return false;
        if (builderFilters.format === 'A4' && template.isPosTemplate) return false;
        if (builderFilters.status === 'ACTIVE' && (template.is_deleted || template.is_active === false)) return false;
        if (builderFilters.status === 'DEFAULT' && !template.is_default) return false;
        if (builderFilters.status === 'INACTIVE' && (template.is_deleted || template.is_active !== false)) return false;
        if (builderFilters.status === 'DELETED' && !template.is_deleted) return false;

        const term = builderFilters.search.trim().toLowerCase();
        if (!term) return true;

        return [
          template.name,
          template.document_type,
          template.locationName,
          template.usedInLabel,
          template.branchLabel,
          template.defaultBadge,
          template.activeBadge,
        ].some((value) => String(value || '').toLowerCase().includes(term));
      })
      .sort((a, b) => {
        const updatedA = new Date(a.updated_at || a.created_at || 0).getTime();
        const updatedB = new Date(b.updated_at || b.created_at || 0).getTime();
        return (
          (moduleOrder[a.primaryModule] ?? 999) - (moduleOrder[b.primaryModule] ?? 999) ||
          a.document_type.localeCompare(b.document_type, 'pt') ||
          getBranchOrder(a.branchLabel) - getBranchOrder(b.branchLabel) ||
          (a.is_default ? 0 : 1) - (b.is_default ? 0 : 1) ||
          (a.is_active === false ? 1 : 0) - (b.is_active === false ? 1 : 0) ||
          updatedB - updatedA ||
          a.name.localeCompare(b.name, 'pt')
        );
      });
  }, [builderFilters, moduleOrder, normalizedTemplates]);

  useEffect(() => {
    if (filteredTemplates.length === 0) {
      setSelectedTemplateId(null);
      return;
    }
    if (!filteredTemplates.some((template) => template.id === selectedTemplateId)) {
      setSelectedTemplateId(filteredTemplates[0].id);
    }
  }, [filteredTemplates, selectedTemplateId]);

  const selectedTemplate = filteredTemplates.find((template) => template.id === selectedTemplateId) || null;

  useEffect(() => {
    let cancelled = false;

    const loadTemplateCompare = async () => {
      if (activePrintTab !== 'builder' || !selectedTemplate) {
        setTemplateCompareData(null);
        return;
      }

      setTemplateCompareLoading(true);
      try {
        const payload = await fetchJsonWithAuth(`/api/templates/compare-resolution/${selectedTemplate.id}`, {}, 'Erro ao carregar comparador do template');
        if (!cancelled) {
          setTemplateCompareData(payload);
        }
      } catch (error) {
        if (!cancelled) {
          setTemplateCompareData(null);
        }
        console.error('Erro ao carregar comparador stored/effective:', error);
      } finally {
        if (!cancelled) {
          setTemplateCompareLoading(false);
        }
      }
    };

    loadTemplateCompare();
    return () => {
      cancelled = true;
    };
  }, [activePrintTab, selectedTemplate]);

  useEffect(() => {
    let cancelled = false;

    const loadTemplateAudit = async () => {
      if (activePrintTab !== 'builder' || !selectedTemplate) {
        setTemplateAuditLogs([]);
        return;
      }

      const templateIds = [selectedTemplate.id, templateCompareData?.effective?.id].filter(Boolean).join(',');
      setTemplateAuditLoading(true);
      try {
        const payload = await fetchJsonWithAuth(`/api/templates/audit/print?template_id=${encodeURIComponent(templateIds)}&limit=12`, {}, 'Erro ao carregar auditoria por template');
        if (!cancelled) {
          setTemplateAuditLogs(payload?.logs || []);
        }
      } catch (error) {
        if (!cancelled) {
          setTemplateAuditLogs([]);
        }
        console.error('Erro ao carregar auditoria por template:', error);
      } finally {
        if (!cancelled) {
          setTemplateAuditLoading(false);
        }
      }
    };

    loadTemplateAudit();
    return () => {
      cancelled = true;
    };
  }, [activePrintTab, selectedTemplate, templateCompareData]);

  useEffect(() => {
    if (!templateAuditLogs.length) {
      setTemplatePreviewMode('sample');
      return;
    }
    setTemplatePreviewMode('real');
  }, [selectedTemplate?.id, templateAuditLogs]);

  const buildTemplateRenderPayload = useCallback((variant, mode = 'sample') => {
    if (!selectedTemplate) return null;

    const latestAudit = templateAuditLogs[0];
    const basePayload = {
      doc_type: selectedTemplate.document_type,
      location_id: selectedTemplate.location_id,
      module_group: selectedTemplate.primaryModule,
      copy_type: 'Original',
    };

    const realPayload = latestAudit ? {
      doc_type: latestAudit.doc_type || selectedTemplate.document_type,
      location_id: latestAudit.location_id || selectedTemplate.location_id,
      module_group: latestAudit.module_group || selectedTemplate.primaryModule,
      sale_id: latestAudit.sale_id || undefined,
      service_id: latestAudit.service_id || undefined,
      quotation_id: latestAudit.quotation_id || undefined,
      payment_id: latestAudit.payment_id || undefined,
      copy_type: 'Original',
    } : null;

    const payload = mode === 'real' && realPayload ? realPayload : basePayload;
    if (variant === 'stored') {
      return { ...payload, template_id: selectedTemplate.id };
    }
    return payload;
  }, [selectedTemplate, templateAuditLogs]);

  useEffect(() => {
    let cancelled = false;
    let storedObjectUrl = null;
    let effectiveObjectUrl = null;

    const loadPreview = async () => {
      if (activePrintTab !== 'builder' || !selectedTemplate) {
        setTemplatePreviewLoading(false);
        setPreviewUrl('');
        setEffectivePreviewUrl('');
        return;
      }

      try {
        setTemplatePreviewLoading(true);
        const storedPayload = buildTemplateRenderPayload('stored', templatePreviewMode);
        const effectivePayload = buildTemplateRenderPayload('effective', templatePreviewMode);

        const [storedResponse, effectiveResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/templates/render-pdf`, {
            method: 'POST',
            headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(storedPayload),
          }),
          fetch(`${API_BASE_URL}/api/templates/render-pdf`, {
            method: 'POST',
            headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(effectivePayload),
          }),
        ]);

        if (!storedResponse.ok || !effectiveResponse.ok) throw new Error('preview_failed');

        const [storedBlob, effectiveBlob] = await Promise.all([storedResponse.blob(), effectiveResponse.blob()]);
        storedObjectUrl = URL.createObjectURL(storedBlob);
        effectiveObjectUrl = URL.createObjectURL(effectiveBlob);

        if (!cancelled) {
          setPreviewUrl(storedObjectUrl);
          setEffectivePreviewUrl(effectiveObjectUrl);
        }
      } catch (error) {
        console.error('Error previewing template PDF:', error);
        if (!cancelled) {
          setPreviewUrl('');
          setEffectivePreviewUrl('');
        }
      } finally {
        if (!cancelled) {
          setTemplatePreviewLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (storedObjectUrl) URL.revokeObjectURL(storedObjectUrl);
      if (effectiveObjectUrl) URL.revokeObjectURL(effectiveObjectUrl);
    };
  }, [activePrintTab, buildTemplateRenderPayload, selectedTemplate, templatePreviewMode]);

  const buildTemplateBuilderUrl = (templateId = null) => {
    const params = new URLSearchParams();
    if (builderFilters.module_group !== 'ALL') params.set('module_group', builderFilters.module_group);
    if (builderFilters.doc_type !== 'ALL') params.set('doc_type', builderFilters.doc_type);
    if (builderFilters.location_id !== 'ALL') params.set('location_id', builderFilters.location_id);
    if (builderFilters.format === 'POS') params.set('context', 'pos');
    if (templateId) params.set('template_id', templateId);
    const query = params.toString();
    return `/template-builder${query ? `?${query}` : ''}`;
  };

  const duplicateTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      const payload = {
        name: `${selectedTemplate.name} (Cópia)`,
        document_type: selectedTemplate.document_type,
        location_id: selectedTemplate.location_id,
        is_default: false,
        module_group: [selectedTemplate.primaryModule],
        freeform_layout: selectedTemplate.freeform_layout,
      };
      await fetchJsonWithAuth('/api/templates/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }, 'Erro ao duplicar template');
      toast.success('Template duplicado');
      clearCachedPrintSettingsPayload();
      loadAll(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao duplicar template'));
    }
  };

  const setTemplateAsDefault = async () => {
    if (!selectedTemplate) return;
    try {
      await fetchJsonWithAuth(`/api/templates/${selectedTemplate.id}/set-default`, {
        method: 'PUT',
      }, 'Erro ao definir template padrão');
      toast.success('Template definido como padrão');
      clearCachedPrintSettingsPayload();
      loadAll(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao definir template padrão'));
    }
  };

  const handleSaveLocationPreset = async () => {
    if (!selectedLocationPresetId) return;
    setSavingLocationPreset(true);
    try {
      await fetchJsonWithAuth('/api/print/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...locationPresetForm,
          is_global: false,
          location_id: selectedLocationPresetId,
        })
      }, 'Erro ao guardar preset da localização');
      setDirtyLocationPreset(false);
      toast.success('Preset da localização guardado');
      clearCachedPrintSettingsPayload();
      loadAll(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar preset da localização'));
    } finally {
      setSavingLocationPreset(false);
    }
  };

  const handleClearLocationPreset = async () => {
    if (!selectedLocationPresetId) return;
    if (!selectedLocationPresetRecord?.id) {
      setLocationPresetForm({
        auto_print_pos: globalSettings.auto_print_pos,
        print_copies: globalSettings.print_copies,
        pos_width: globalSettings.pos_width,
        show_signature_line: globalSettings.show_signature_line,
        show_qr_code: globalSettings.show_qr_code,
        footer_text: globalSettings.footer_text,
      });
      setDirtyLocationPreset(false);
      return;
    }

    setSavingLocationPreset(true);
    try {
      await fetchJsonWithAuth(`/api/print/settings/${selectedLocationPresetRecord.id}`, {
        method: 'DELETE',
      }, 'Erro ao limpar override da localização');
      toast.success('Override da localização removido');
      clearCachedPrintSettingsPayload();
      loadAll(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao limpar override da localização'));
    } finally {
      setSavingLocationPreset(false);
    }
  };

  const exportAuditCsv = () => {
    const rows = [
      ['timestamp', 'doc_type', 'location_id', 'module_group', 'resolved_template_name', 'resolved_template_rule', 'user_email'],
      ...auditLogs.map((log) => [
        log.timestamp,
        log.doc_type,
        log.location_id || '',
        log.module_group || '',
        log.resolved_template_name || '',
        log.resolved_template_rule || '',
        log.user_email || '',
      ])
    ];

    const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? '').replaceAll('"', '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `auditoria-impressao-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="print-settings-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const hasSettingsData = Boolean(
    locations.length ||
    printSettings.length ||
    templates.length ||
    auditLogs.length ||
    previewUrl
  );

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={hasSettingsData || partialIssues.length > 0}
      partialIssues={partialIssues}
      loadingTestId="print-settings-loading"
      partialIssuesTestId="print-settings-partial-issues"
      emptyState={
        <EmptyStatePanel
          title="Sem dados de impressão"
          description="As configurações de impressão abriram sem conteúdo. Pode recarregar para voltar a buscar templates e políticas globais."
          actionLabel="Recarregar"
          onAction={loadAll}
          testId="print-settings-empty-state"
        />
      }
    >
    <div className="space-y-4" data-testid="print-settings-tab">
      <div className="rounded-3xl border bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 shadow-sm" data-testid="print-cockpit-shell">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2" data-testid="print-cockpit-title">
              <Settings className="h-5 w-5" />
              Settings &gt; Prints
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="print-cockpit-tabs">
            {[
              { key: 'presets', label: 'Presets' },
              { key: 'builder', label: 'Template Builder' },
              { key: 'catalog', label: 'Catálogo ERP' },
              { key: 'diagnostics', label: 'Diagnóstico' },
              { key: 'audit', label: 'Auditoria' },
            ].map((tab) => (
              <Button
                key={tab.key}
                variant={activePrintTab === tab.key ? 'default' : 'outline'}
                className="h-8 rounded-full px-3 text-xs"
                onClick={() => switchPrintTab(tab.key)}
                data-testid={`print-cockpit-tab-${tab.key}`}
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-start gap-1.5 max-w-[980px]" data-testid="print-settings-compact-summary-strip">
          <Card className="w-[148px] shadow-none" data-testid="print-summary-total-templates">
            <CardContent className="flex h-[62px] items-center justify-between gap-2 px-3 py-1.5">
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Templates registados</p>
              <p className="text-lg font-bold leading-none">{cockpitStats.total}</p>
            </CardContent>
          </Card>
          <Card className="w-[148px] shadow-none" data-testid="print-summary-active-templates">
            <CardContent className="flex h-[62px] items-center justify-between gap-2 px-3 py-1.5">
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Activos</p>
              <p className="text-lg font-bold leading-none">{cockpitStats.active}</p>
            </CardContent>
          </Card>
          <Card className="w-[148px] shadow-none" data-testid="print-summary-default-templates">
            <CardContent className="flex h-[62px] items-center justify-between gap-2 px-3 py-1.5">
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Padrão</p>
              <p className="text-lg font-bold leading-none">{cockpitStats.defaults}</p>
            </CardContent>
          </Card>
          <Card className="w-[148px] shadow-none" data-testid="print-summary-covered-branches">
            <CardContent className="flex h-[62px] items-center justify-between gap-2 px-3 py-1.5">
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Filiais com escopo</p>
              <p className="text-lg font-bold leading-none">{cockpitStats.branches}</p>
            </CardContent>
          </Card>
          <Card className="w-[148px] shadow-none" data-testid="print-summary-covered-doc-types">
            <CardContent className="flex h-[62px] items-center justify-between gap-2 px-3 py-1.5">
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Doc types cobertos</p>
              <p className="text-lg font-bold leading-none">{cockpitStats.docTypes}</p>
            </CardContent>
          </Card>
          <Card className="w-[148px] shadow-none" data-testid="print-summary-physical-templates">
            <CardContent className="flex h-[62px] items-center justify-between gap-2 px-3 py-1.5">
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Físicos BD</p>
              <p className="text-lg font-bold leading-none">{diagnosticsSummary.physical_templates || 0}</p>
            </CardContent>
          </Card>
          <Card className="w-[148px] shadow-none" data-testid="print-summary-effective-templates">
            <CardContent className="flex h-[62px] items-center justify-between gap-2 px-3 py-1.5">
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Efectivos</p>
              <p className="text-lg font-bold leading-none">{diagnosticsSummary.effective_templates || 0}</p>
            </CardContent>
          </Card>
          <Card className="w-[148px] shadow-none" data-testid="print-summary-duplicate-scopes">
            <CardContent className="flex h-[62px] items-center justify-between gap-2 px-3 py-1.5">
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Duplicados</p>
              <p className="text-lg font-bold leading-none">{diagnosticsSummary.duplicate_scopes || 0}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {activePrintTab === 'presets' && (
        <PrintPresetsUnifiedCockpit
          addCompanyAdditionalBank={addCompanyAdditionalBank}
          addLocationAdditionalBank={addLocationAdditionalBank}
          cockpitStats={cockpitStats}
          companyBanking={companyBanking}
          diagnosticsSummary={diagnosticsSummary}
          dirtyCompanyBanking={dirtyCompanyBanking}
          dirtyGlobal={dirtyGlobal}
          dirtyLocationBanking={dirtyLocationBanking}
          dirtyLocationPreset={dirtyLocationPreset}
          globalSettings={globalSettings}
          handleClearLocationBanking={handleClearLocationBanking}
          handleCompanyAdditionalBankChange={handleCompanyAdditionalBankChange}
          handleCompanyBankingFieldChange={handleCompanyBankingFieldChange}
          handleGlobalFieldChange={handleGlobalFieldChange}
          handleLocationAdditionalBankChange={handleLocationAdditionalBankChange}
          handleLocationBankingFieldChange={handleLocationBankingFieldChange}
          handleLocationPresetFieldChange={handleLocationPresetFieldChange}
          handleSaveCompanyBanking={handleSaveCompanyBanking}
          handleSaveGlobalSettings={handleSaveGlobalSettings}
          handleSaveLocationBanking={handleSaveLocationBanking}
          handleSaveLocationPreset={handleSaveLocationPreset}
          jumpToBuilder={jumpToBuilder}
          locationBankingForm={locationBankingForm}
          locationBankingList={locationBankingList}
          locationCoverage={locationCoverage}
          locationPresetForm={locationPresetForm}
          onClearLocationPreset={handleClearLocationPreset}
          printSettings={printSettings}
          printCompareData={printCompareData}
          printCompareLoading={printCompareLoading}
          removeCompanyAdditionalBank={removeCompanyAdditionalBank}
          removeLocationAdditionalBank={removeLocationAdditionalBank}
          saving={saving}
          savingBanking={savingBanking}
          savingLocationPreset={savingLocationPreset}
          selectedBankingLocationId={selectedBankingLocationId}
          selectedLocationPresetId={selectedLocationPresetId}
          selectedLocationPresetRecord={selectedLocationPresetRecord}
          selectedLocationPresetSummary={selectedLocationPresetSummary}
          setSelectedBankingLocationId={setSelectedBankingLocationId}
          setSelectedLocationPresetId={setSelectedLocationPresetId}
        />
      )}

      {activePrintTab === 'globals' && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.8fr)]" data-testid="print-globals-grid">
          <Card>
            <CardHeader>
              <CardTitle data-testid="globals-settings-title">Políticas globais de impressão</CardTitle>
              <CardDescription data-testid="globals-settings-description">Defina o comportamento padrão do POS e o rodapé comum para documentos térmicos.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between rounded-2xl border p-4" data-testid="global-setting-auto-print-pos">
                  <div>
                    <p className="font-medium">Auto-imprimir POS</p>
                    <p className="text-xs text-muted-foreground">Após a confirmação da venda no caixa.</p>
                  </div>
                  <Switch checked={globalSettings.auto_print_pos} onCheckedChange={(value) => handleGlobalFieldChange({ auto_print_pos: value })} data-testid="global-auto-print-switch" />
                </div>
                <div className="flex items-center justify-between rounded-2xl border p-4" data-testid="global-setting-print-copies">
                  <div>
                    <p className="font-medium">Nº de vias POS</p>
                    <p className="text-xs text-muted-foreground">Definido como padrão para recibos térmicos.</p>
                  </div>
                  <Select value={String(globalSettings.print_copies)} onValueChange={(value) => handleGlobalFieldChange({ print_copies: parseInt(value, 10) })}>
                    <SelectTrigger className="w-24" data-testid="global-print-copies-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between rounded-2xl border p-4" data-testid="global-setting-pos-width">
                  <div>
                    <p className="font-medium">Largura POS</p>
                    <p className="text-xs text-muted-foreground">Seleccione a impressora térmica padrão.</p>
                  </div>
                  <Select value={globalSettings.pos_width} onValueChange={(value) => handleGlobalFieldChange({ pos_width: value })}>
                    <SelectTrigger className="w-24" data-testid="global-pos-width-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="58mm">58mm</SelectItem>
                      <SelectItem value="80mm">80mm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between rounded-2xl border p-4" data-testid="global-setting-signature-line">
                  <div>
                    <p className="font-medium">Linha de assinatura</p>
                    <p className="text-xs text-muted-foreground">Aplicável aos documentos que usam assinatura manual.</p>
                  </div>
                  <Switch checked={globalSettings.show_signature_line} onCheckedChange={(value) => handleGlobalFieldChange({ show_signature_line: value })} data-testid="global-signature-line-switch" />
                </div>
                <div className="flex items-center justify-between rounded-2xl border p-4 md:col-span-2" data-testid="global-setting-qr-code">
                  <div>
                    <p className="font-medium">QR Code</p>
                    <p className="text-xs text-muted-foreground">Mantém a política global pronta para futuros layouts com QR.</p>
                  </div>
                  <Switch checked={globalSettings.show_qr_code} onCheckedChange={(value) => handleGlobalFieldChange({ show_qr_code: value })} data-testid="global-qr-code-switch" />
                </div>
              </div>

              <div className="rounded-2xl border p-4" data-testid="global-setting-footer-text">
                <Label htmlFor="global-footer-text" className="font-medium">Texto do rodapé POS</Label>
                <Textarea id="global-footer-text" value={globalSettings.footer_text || ''} onChange={(event) => handleGlobalFieldChange({ footer_text: event.target.value })} rows={3} className="mt-3" data-testid="global-footer-textarea" />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={handleSaveGlobalSettings} disabled={saving} data-testid="save-global-print-settings">
                  {saving ? 'A guardar...' : 'Guardar configurações globais'}
                </Button>
                <Button variant="outline" onClick={() => jumpToBuilder()} data-testid="go-to-template-builder">
                  Abrir cockpit de templates
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="globals-context-card">
            <CardHeader>
              <CardTitle>Resumo operacional</CardTitle>
              <CardDescription>Leitura rápida para decisões antes de entrar no Template Builder.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="rounded-2xl border p-4" data-testid="globals-context-active-templates">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Templates activos</p>
                <p className="mt-2 text-2xl font-semibold">{cockpitStats.active}</p>
              </div>
              <div className="rounded-2xl border p-4" data-testid="globals-context-branch-coverage">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Filiais cobertas</p>
                <p className="mt-2 text-2xl font-semibold">{cockpitStats.branches}</p>
              </div>
              <div className="rounded-2xl border p-4" data-testid="globals-context-default-note">
                <p className="font-medium">Ponto de atenção</p>
                <p className="mt-2 text-muted-foreground">Esta Entrega 1 organiza e profissionaliza a gestão. Não altera deduplicação nem hard delete estrutural.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activePrintTab === 'banking' && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="print-banking-grid">
          <Card data-testid="print-banking-company-card">
            <CardHeader>
              <CardTitle>Dados Bancários Globais</CardTitle>
              <CardDescription>Estes dados alimentam `company.*` nos templates de impressão e servem como base para todas as filiais.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="company-bank-account-holder">Titular</Label>
                  <Input id="company-bank-account-holder" value={companyBanking.bank_account_holder} onChange={(event) => handleCompanyBankingFieldChange({ bank_account_holder: event.target.value })} data-testid="company-bank-account-holder-input" />
                </div>
                <div>
                  <Label htmlFor="company-bank-name">Banco</Label>
                  <Input id="company-bank-name" value={companyBanking.bank_name} onChange={(event) => handleCompanyBankingFieldChange({ bank_name: event.target.value })} data-testid="company-bank-name-input" />
                </div>
                <div>
                  <Label htmlFor="company-bank-account-number">Número da Conta</Label>
                  <Input id="company-bank-account-number" value={companyBanking.bank_account_number} onChange={(event) => handleCompanyBankingFieldChange({ bank_account_number: event.target.value })} data-testid="company-bank-account-number-input" />
                </div>
                <div>
                  <Label htmlFor="company-bank-nib">NIB</Label>
                  <Input id="company-bank-nib" value={companyBanking.bank_nib} onChange={(event) => handleCompanyBankingFieldChange({ bank_nib: event.target.value })} data-testid="company-bank-nib-input" />
                </div>
                <div>
                  <Label htmlFor="company-bank-iban">IBAN</Label>
                  <Input id="company-bank-iban" value={companyBanking.bank_iban} onChange={(event) => handleCompanyBankingFieldChange({ bank_iban: event.target.value })} data-testid="company-bank-iban-input" />
                </div>
                <div>
                  <Label htmlFor="company-bank-swift">SWIFT</Label>
                  <Input id="company-bank-swift" value={companyBanking.bank_swift} onChange={(event) => handleCompanyBankingFieldChange({ bank_swift: event.target.value })} data-testid="company-bank-swift-input" />
                </div>
              </div>
              <div className="space-y-3" data-testid="company-additional-banks-list">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-medium">2º / 3º banco e adicionais</p>
                    <p className="text-xs text-muted-foreground">Use estes blocos para configurar bancos extra além do banco principal.</p>
                  </div>
                  <Button type="button" variant="outline" onClick={addCompanyAdditionalBank} data-testid="add-company-additional-bank-button">Adicionar banco</Button>
                </div>
                {(companyBanking.additional_banks || []).map((bank, index) => (
                  <div key={`company-extra-bank-${index}`} className="rounded-2xl border p-4 space-y-3" data-testid={`company-additional-bank-card-${index}`}>
                    <div className="flex items-center justify-between gap-2">
                      <Input value={bank.label || `Banco ${index + 2}`} onChange={(event) => handleCompanyAdditionalBankChange(index, { label: event.target.value })} className="max-w-[220px]" data-testid={`company-additional-bank-label-${index}`} />
                      <Button type="button" variant="ghost" onClick={() => removeCompanyAdditionalBank(index)} data-testid={`remove-company-additional-bank-${index}`}>Remover</Button>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <Input value={bank.bank_account_holder || ''} onChange={(event) => handleCompanyAdditionalBankChange(index, { bank_account_holder: event.target.value })} placeholder="Titular" data-testid={`company-additional-bank-holder-${index}`} />
                      <Input value={bank.bank_name || ''} onChange={(event) => handleCompanyAdditionalBankChange(index, { bank_name: event.target.value })} placeholder="Banco" data-testid={`company-additional-bank-name-${index}`} />
                      <Input value={bank.bank_account_number || ''} onChange={(event) => handleCompanyAdditionalBankChange(index, { bank_account_number: event.target.value })} placeholder="Número da Conta" data-testid={`company-additional-bank-account-number-${index}`} />
                      <Input value={bank.bank_nib || ''} onChange={(event) => handleCompanyAdditionalBankChange(index, { bank_nib: event.target.value })} placeholder="NIB" data-testid={`company-additional-bank-nib-${index}`} />
                      <Input value={bank.bank_iban || ''} onChange={(event) => handleCompanyAdditionalBankChange(index, { bank_iban: event.target.value })} placeholder="IBAN" data-testid={`company-additional-bank-iban-${index}`} />
                      <Input value={bank.bank_swift || ''} onChange={(event) => handleCompanyAdditionalBankChange(index, { bank_swift: event.target.value })} placeholder="SWIFT" data-testid={`company-additional-bank-swift-${index}`} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleSaveCompanyBanking} disabled={savingBanking === 'company' || !dirtyCompanyBanking} data-testid="save-company-banking-button">
                  {savingBanking === 'company' ? 'A guardar...' : 'Guardar dados bancários globais'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="print-banking-location-card">
            <CardHeader>
              <CardTitle>Dados Bancários por Localização</CardTitle>
              <CardDescription>Defina override por filial. Se deixar vazio, a localização herda os dados globais.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="print-banking-location-select">Localização</Label>
                <Select value={selectedBankingLocationId || undefined} onValueChange={setSelectedBankingLocationId}>
                  <SelectTrigger id="print-banking-location-select" data-testid="print-banking-location-select"><SelectValue placeholder="Escolha uma localização" /></SelectTrigger>
                  <SelectContent>
                    {locationBankingList.map((location) => (
                      <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedBankingLocationId && (() => {
                const selectedLocation = locationBankingList.find((item) => item.id === selectedBankingLocationId);
                return selectedLocation ? (
                  <>
                    <div className="rounded-2xl border p-4 text-sm" data-testid="print-banking-location-effective-card">
                      <p className="font-medium">Dados efectivos na impressão</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {selectedLocation.uses_global_defaults ? 'Esta localização está a usar os dados globais.' : 'Esta localização tem override próprio sobre os dados globais.'}
                      </p>
                      <div className="mt-3 grid gap-2 text-xs md:grid-cols-2">
                        <p><span className="font-medium">Titular:</span> {selectedLocation.effective?.bank_account_holder || '—'}</p>
                        <p><span className="font-medium">Banco:</span> {selectedLocation.effective?.bank_name || '—'}</p>
                        <p><span className="font-medium">Conta:</span> {selectedLocation.effective?.bank_account_number || '—'}</p>
                        <p><span className="font-medium">NIB:</span> {selectedLocation.effective?.bank_nib || '—'}</p>
                        <p><span className="font-medium">IBAN:</span> {selectedLocation.effective?.bank_iban || '—'}</p>
                        <p><span className="font-medium">SWIFT:</span> {selectedLocation.effective?.bank_swift || '—'}</p>
                      </div>
                      {selectedLocation.effective?.banks?.length > 1 && (
                        <div className="mt-3 space-y-2" data-testid="print-banking-location-effective-banks-list">
                          {selectedLocation.effective.banks.map((bank, index) => (
                            <div key={`effective-bank-${index}`} className="rounded-xl border bg-muted/30 p-3 text-xs" data-testid={`effective-bank-card-${index}`}>
                              <p className="font-medium">{bank.label || `Banco ${index + 1}`}</p>
                              <p className="mt-1 text-muted-foreground">{bank.bank_name || 'Sem banco'} · {bank.bank_account_number || 'Sem conta'} · {bank.bank_swift || 'Sem SWIFT'}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label htmlFor="location-bank-account-holder">Titular</Label>
                        <Input id="location-bank-account-holder" value={locationBankingForm.bank_account_holder} onChange={(event) => handleLocationBankingFieldChange({ bank_account_holder: event.target.value })} data-testid="location-bank-account-holder-input" />
                      </div>
                      <div>
                        <Label htmlFor="location-bank-name">Banco</Label>
                        <Input id="location-bank-name" value={locationBankingForm.bank_name} onChange={(event) => handleLocationBankingFieldChange({ bank_name: event.target.value })} data-testid="location-bank-name-input" />
                      </div>
                      <div>
                        <Label htmlFor="location-bank-account-number">Número da Conta</Label>
                        <Input id="location-bank-account-number" value={locationBankingForm.bank_account_number} onChange={(event) => handleLocationBankingFieldChange({ bank_account_number: event.target.value })} data-testid="location-bank-account-number-input" />
                      </div>
                      <div>
                        <Label htmlFor="location-bank-nib">NIB</Label>
                        <Input id="location-bank-nib" value={locationBankingForm.bank_nib} onChange={(event) => handleLocationBankingFieldChange({ bank_nib: event.target.value })} data-testid="location-bank-nib-input" />
                      </div>
                      <div>
                        <Label htmlFor="location-bank-iban">IBAN</Label>
                        <Input id="location-bank-iban" value={locationBankingForm.bank_iban} onChange={(event) => handleLocationBankingFieldChange({ bank_iban: event.target.value })} data-testid="location-bank-iban-input" />
                      </div>
                      <div>
                        <Label htmlFor="location-bank-swift">SWIFT</Label>
                        <Input id="location-bank-swift" value={locationBankingForm.bank_swift} onChange={(event) => handleLocationBankingFieldChange({ bank_swift: event.target.value })} data-testid="location-bank-swift-input" />
                      </div>
                    </div>
                    <div className="space-y-3" data-testid="location-additional-banks-list">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="font-medium">Bancos extra da localização</p>
                          <p className="text-xs text-muted-foreground">Se preencher aqui, esta filial pode mostrar 2º, 3º ou mais bancos no documento.</p>
                        </div>
                        <Button type="button" variant="outline" onClick={addLocationAdditionalBank} data-testid="add-location-additional-bank-button">Adicionar banco</Button>
                      </div>
                      {(locationBankingForm.additional_banks || []).map((bank, index) => (
                        <div key={`location-extra-bank-${index}`} className="rounded-2xl border p-4 space-y-3" data-testid={`location-additional-bank-card-${index}`}>
                          <div className="flex items-center justify-between gap-2">
                            <Input value={bank.label || `Banco ${index + 2}`} onChange={(event) => handleLocationAdditionalBankChange(index, { label: event.target.value })} className="max-w-[220px]" data-testid={`location-additional-bank-label-${index}`} />
                            <Button type="button" variant="ghost" onClick={() => removeLocationAdditionalBank(index)} data-testid={`remove-location-additional-bank-${index}`}>Remover</Button>
                          </div>
                          <div className="grid gap-4 md:grid-cols-2">
                            <Input value={bank.bank_account_holder || ''} onChange={(event) => handleLocationAdditionalBankChange(index, { bank_account_holder: event.target.value })} placeholder="Titular" data-testid={`location-additional-bank-holder-${index}`} />
                            <Input value={bank.bank_name || ''} onChange={(event) => handleLocationAdditionalBankChange(index, { bank_name: event.target.value })} placeholder="Banco" data-testid={`location-additional-bank-name-${index}`} />
                            <Input value={bank.bank_account_number || ''} onChange={(event) => handleLocationAdditionalBankChange(index, { bank_account_number: event.target.value })} placeholder="Número da Conta" data-testid={`location-additional-bank-account-number-${index}`} />
                            <Input value={bank.bank_nib || ''} onChange={(event) => handleLocationAdditionalBankChange(index, { bank_nib: event.target.value })} placeholder="NIB" data-testid={`location-additional-bank-nib-${index}`} />
                            <Input value={bank.bank_iban || ''} onChange={(event) => handleLocationAdditionalBankChange(index, { bank_iban: event.target.value })} placeholder="IBAN" data-testid={`location-additional-bank-iban-${index}`} />
                            <Input value={bank.bank_swift || ''} onChange={(event) => handleLocationAdditionalBankChange(index, { bank_swift: event.target.value })} placeholder="SWIFT" data-testid={`location-additional-bank-swift-${index}`} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={handleSaveLocationBanking} disabled={savingBanking === 'location' || !dirtyLocationBanking} data-testid="save-location-banking-button">
                        {savingBanking === 'location' ? 'A guardar...' : 'Guardar override da localização'}
                      </Button>
                      <Button variant="outline" onClick={handleClearLocationBanking} disabled={savingBanking === 'location'} data-testid="clear-location-banking-button">
                        Limpar override e usar global
                      </Button>
                    </div>
                  </>
                ) : null;
              })()}
            </CardContent>
          </Card>
        </div>
      )}

      {activePrintTab === 'locations' && (
        <Card data-testid="print-locations-card">
          <CardHeader>
            <CardTitle>Cobertura por localização</CardTitle>
            <CardDescription>Veja rapidamente quantos templates POS/A4 existem por filial e salte para o cockpit filtrado.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filial</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Templates POS</TableHead>
                  <TableHead>Templates A4</TableHead>
                  <TableHead>Padrão</TableHead>
                  <TableHead className="text-right">Acções</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locationCoverage.map((location) => {
                  const setting = printSettings.find((item) => item.location_id === location.id);
                  return (
                    <TableRow key={location.id} data-testid={`print-location-row-${location.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium" data-testid={`print-location-name-${location.id}`}>{location.name}</p>
                          <p className="text-xs text-muted-foreground" data-testid={`print-location-pos-width-${location.id}`}>POS padrão: {setting?.pos_width || globalSettings.pos_width}</p>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" data-testid={`print-location-type-${location.id}`}>{location.type || 'store'}</Badge></TableCell>
                      <TableCell data-testid={`print-location-pos-count-${location.id}`}>{location.posCount}</TableCell>
                      <TableCell data-testid={`print-location-a4-count-${location.id}`}>{location.a4Count}</TableCell>
                      <TableCell data-testid={`print-location-default-count-${location.id}`}>{location.defaultCount}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => jumpToBuilder({ location_id: location.id, format: 'POS' })} data-testid={`set-pos-template-${location.id}`}>POS</Button>
                          <Button size="sm" variant="outline" onClick={() => jumpToBuilder({ location_id: location.id, format: 'A4' })} data-testid={`set-a4-template-${location.id}`}>A4</Button>
                          <Button size="sm" variant="outline" onClick={() => jumpToBuilder({ location_id: location.id })} data-testid={`view-templates-${location.id}`}>Ver todos</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {activePrintTab === 'builder' && (
        <Card ref={builderCardRef} data-testid="print-builder-control-center">
          <CardHeader>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <CardTitle>Template Builder</CardTitle>
                <CardDescription>
                  Listagem organizada por módulo, doc_type e filial, com estado visível e filtros persistentes.
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={resetBuilderFilters} data-testid="builder-clear-persistent-filters-btn">Limpar filtros</Button>
                <Button variant="outline" onClick={() => navigate(buildTemplateBuilderUrl(selectedTemplate?.id))} data-testid="builder-open-editor-btn">Abrir editor completo</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6" data-testid="print-builder-controls">
              <div>
                <Label className="text-xs text-muted-foreground">Filial</Label>
                <Select value={builderFilters.location_id} onValueChange={(value) => updateBuilderFilters({ location_id: value })}>
                  <SelectTrigger data-testid="builder-filter-branch"><SelectValue placeholder="Filial" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todas as filiais</SelectItem>
                    <SelectItem value="GLOBAL">Global</SelectItem>
                    {locations.map((location) => <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Doc type</Label>
                <Select value={builderFilters.doc_type} onValueChange={(value) => updateBuilderFilters({ doc_type: value })}>
                  <SelectTrigger data-testid="builder-filter-doc-type"><SelectValue placeholder="Doc type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    {uniqueDocTypes.map((docType) => <SelectItem key={docType} value={docType}>{docType}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Usado em</Label>
                <Select value={builderFilters.module_group} onValueChange={(value) => updateBuilderFilters({ module_group: value })}>
                  <SelectTrigger data-testid="builder-filter-module"><SelectValue placeholder="Módulo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    {moduleOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Formato</Label>
                <Select value={builderFilters.format} onValueChange={(value) => updateBuilderFilters({ format: value })}>
                  <SelectTrigger data-testid="builder-filter-format"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">POS + A4</SelectItem>
                    <SelectItem value="POS">POS</SelectItem>
                    <SelectItem value="A4">A4</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Estado</Label>
                <Select value={builderFilters.status} onValueChange={(value) => updateBuilderFilters({ status: value })}>
                  <SelectTrigger data-testid="builder-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    <SelectItem value="ACTIVE">Activos</SelectItem>
                    <SelectItem value="DEFAULT">Padrão</SelectItem>
                    <SelectItem value="INACTIVE">Inactivos</SelectItem>
                    <SelectItem value="DELETED">Apagados</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Pesquisa</Label>
                <Input value={builderFilters.search} onChange={(event) => updateBuilderFilters({ search: event.target.value })} placeholder="Nome, doc_type, filial..." data-testid="builder-filter-search" />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2" data-testid="builder-persistent-filter-badges">
              <Badge variant="secondary" data-testid="builder-filter-badge-branch">Filial: {builderFilters.location_id}</Badge>
              <Badge variant="secondary" data-testid="builder-filter-badge-doc-type">Doc type: {builderFilters.doc_type}</Badge>
              <Badge variant="secondary" data-testid="builder-filter-badge-module">Usado em: {builderFilters.module_group}</Badge>
              <Badge variant="secondary" data-testid="builder-filter-badge-status">Estado: {builderFilters.status}</Badge>
              <Badge variant="outline" data-testid="builder-filter-persistence-note">Persistente após refresh</Badge>
            </div>

            <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.85fr)]">
              <div className="overflow-hidden rounded-2xl border" data-testid="builder-template-list-pane">
                <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1.2fr)] gap-3 border-b bg-muted/40 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" data-testid="builder-template-table-header">
                  <span>Template</span>
                  <span>Usado em</span>
                  <span>Doc type</span>
                  <span>Filial</span>
                  <span>Estado / updated_at</span>
                </div>
                <div className="max-h-[620px] overflow-auto">
                  {filteredTemplates.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="builder-empty-state">Nenhum template encontrado para os filtros actuais.</div>
                  ) : (
                    filteredTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setSelectedTemplateId(template.id)}
                        className={cn(
                          'grid w-full grid-cols-[minmax(0,1.6fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1.2fr)] gap-3 border-b px-4 py-4 text-left transition-colors',
                          selectedTemplate?.id === template.id ? 'bg-primary/5' : 'hover:bg-muted/30'
                        )}
                        data-testid={`builder-template-row-${template.id}`}
                      >
                        <div className="min-w-0" data-testid={`builder-template-name-cell-${template.id}`}>
                          <p className="truncate font-medium">{template.name}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{template.isPosTemplate ? 'POS' : 'A4'} · {template.id}</p>
                        </div>
                        <div className="min-w-0" data-testid={`builder-template-module-cell-${template.id}`}>
                          <Badge className={template.moduleMeta.badgeClass}>{template.usedInLabel}</Badge>
                          {template.isModuleLegacy && <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-900" data-testid={`builder-template-legacy-module-${template.id}`}>Legacy</Badge>}
                        </div>
                        <div className="min-w-0" data-testid={`builder-template-doc-type-cell-${template.id}`}>
                          <p className="font-medium">{template.document_type}</p>
                        </div>
                        <div className="min-w-0" data-testid={`builder-template-branch-cell-${template.id}`}>
                          <p className="truncate font-medium">{template.locationName}</p>
                          <Badge variant="outline" className="mt-2 text-[10px]">{template.branchLabel}</Badge>
                        </div>
                        <div className="min-w-0 space-y-2" data-testid={`builder-template-status-cell-${template.id}`}>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant={template.is_default ? 'default' : 'secondary'} data-testid={`builder-template-default-badge-${template.id}`}>{template.defaultBadge}</Badge>
                            <Badge variant={template.is_deleted || template.is_active === false ? 'destructive' : 'outline'} data-testid={`builder-template-active-badge-${template.id}`}>{template.activeBadge}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground" data-testid={`builder-template-updated-${template.id}`}>{template.updatedLabel}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div className="space-y-4" data-testid="builder-editor-preview-pane">
                <Card>
                  <CardHeader>
                    <CardTitle data-testid="builder-selected-template-title">{selectedTemplate ? selectedTemplate.name : 'Seleccione um template'}</CardTitle>
                    <CardDescription data-testid="builder-selected-template-description">
                      {selectedTemplate ? 'Resumo do template seleccionado e acesso rápido ao editor completo.' : 'Os detalhes aparecem aqui depois de seleccionar uma linha na listagem.'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedTemplate ? (
                      <>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="rounded-2xl border p-3" data-testid="selected-template-used-in-card">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">Usado em</p>
                            <div className="mt-2"><Badge className={selectedTemplate.moduleMeta.badgeClass}>{selectedTemplate.usedInLabel}</Badge></div>
                            {selectedTemplate.isModuleLegacy && <p className="mt-2 text-xs text-amber-800">Module group físico legacy; origem efectiva governada: {selectedTemplate.originContext}</p>}
                          </div>
                          <div className="rounded-2xl border p-3" data-testid="selected-template-doc-type-card">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">Doc type</p>
                            <p className="mt-2 font-medium">{selectedTemplate.document_type}</p>
                          </div>
                          <div className="rounded-2xl border p-3" data-testid="selected-template-branch-card">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">Filial</p>
                            <p className="mt-2 font-medium">{selectedTemplate.locationName}</p>
                          </div>
                          <div className="rounded-2xl border p-3" data-testid="selected-template-status-card">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">Estado</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <Badge variant={selectedTemplate.is_default ? 'default' : 'secondary'}>{selectedTemplate.defaultBadge}</Badge>
                              <Badge variant={selectedTemplate.is_deleted || selectedTemplate.is_active === false ? 'destructive' : 'outline'}>{selectedTemplate.activeBadge}</Badge>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-2xl border p-3" data-testid="selected-template-updated-card">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">updated_at</p>
                          <p className="mt-2 font-medium">{selectedTemplate.updatedLabel}</p>
                        </div>

                        <div className="flex flex-wrap gap-2" data-testid="builder-action-buttons-row">
                          <Button variant="outline" onClick={duplicateTemplate} data-testid="builder-duplicate-btn">Duplicar</Button>
                          <Button variant="outline" onClick={setTemplateAsDefault} data-testid="builder-set-default-btn">Definir como padrão</Button>
                          <Button onClick={() => navigate(buildTemplateBuilderUrl(selectedTemplate.id))} data-testid="builder-edit-selected-btn">Editar no Template Builder</Button>
                        </div>
                      </>
                    ) : null}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Pré-visualização</CardTitle>
                    <CardDescription>Renderização rápida do template guardado no modo actual para verificação instantânea.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[440px] overflow-hidden rounded-2xl border bg-white" data-testid="builder-preview-shell">
                      {templatePreviewLoading ? (
                        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground" data-testid="builder-preview-loading-state">
                          A gerar preview do template seleccionado...
                        </div>
                      ) : previewUrl ? (
                        <iframe title="preview-template" src={previewUrl} className="h-full w-full" data-testid="builder-live-preview-frame" />
                      ) : (
                        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground" data-testid="builder-preview-empty-state">
                          Sem preview disponível para os filtros actuais.
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <TemplateStoredEffectiveComparator
                  selectedTemplate={selectedTemplate}
                  compareData={templateCompareData}
                  loading={templateCompareLoading}
                  storedPreviewUrl={previewUrl}
                  effectivePreviewUrl={effectivePreviewUrl}
                  previewLoading={templatePreviewLoading}
                  previewMode={templatePreviewMode}
                  onPreviewModeChange={setTemplatePreviewMode}
                  auditLogs={templateAuditLogs}
                  auditLoading={templateAuditLoading}
                  onFocusEffectiveTemplate={() => {
                    const effectiveTemplate = normalizedTemplates.find((template) => template.id === templateCompareData?.effective?.id);
                    if (effectiveTemplate) {
                      setBuilderFilters((prev) => ({
                        ...prev,
                        location_id: effectiveTemplate.location_id || 'GLOBAL',
                        doc_type: effectiveTemplate.document_type || 'ALL',
                        module_group: effectiveTemplate.primaryModule || 'ALL',
                        format: effectiveTemplate.isPosTemplate ? 'POS' : 'A4',
                        status: 'ALL',
                      }));
                      setSelectedTemplateId(effectiveTemplate.id);
                    }
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {activePrintTab === 'audit' && (
        <Card data-testid="print-audit-card">
          <CardHeader>
            <CardTitle>Auditoria de impressão</CardTitle>
            <CardDescription>Consulte a regra de resolução aplicada por doc_type, filial e módulo.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-6" data-testid="print-audit-filters">
              <Input type="date" value={auditFilters.dateFrom} onChange={(event) => setAuditFilters((prev) => ({ ...prev, dateFrom: event.target.value }))} data-testid="audit-filter-date-from" />
              <Input type="date" value={auditFilters.dateTo} onChange={(event) => setAuditFilters((prev) => ({ ...prev, dateTo: event.target.value }))} data-testid="audit-filter-date-to" />
              <Select value={auditFilters.location_id} onValueChange={(value) => setAuditFilters((prev) => ({ ...prev, location_id: value }))}>
                <SelectTrigger data-testid="audit-filter-location"><SelectValue placeholder="Filial" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todas filiais</SelectItem>
                  {locations.map((location) => <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={auditFilters.doc_type} onValueChange={(value) => setAuditFilters((prev) => ({ ...prev, doc_type: value }))}>
                <SelectTrigger data-testid="audit-filter-doc-type"><SelectValue placeholder="Doc" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos docs</SelectItem>
                  {uniqueDocTypes.map((docType) => <SelectItem key={docType} value={docType}>{docType}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={auditFilters.module_group} onValueChange={(value) => setAuditFilters((prev) => ({ ...prev, module_group: value }))}>
                <SelectTrigger data-testid="audit-filter-module"><SelectValue placeholder="Módulo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos módulos</SelectItem>
                  {moduleOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button variant="outline" onClick={runAuditSearch} data-testid="audit-apply-filters-btn">Filtrar</Button>
                <Button onClick={exportAuditCsv} data-testid="audit-export-csv-btn">Export CSV</Button>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Doc</TableHead>
                  <TableHead>Filial</TableHead>
                  <TableHead>Módulo</TableHead>
                  <TableHead>Template resolvido</TableHead>
                  <TableHead>Regra</TableHead>
                  <TableHead>Utilizador</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground" data-testid="audit-empty-state">Sem registos de auditoria</TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log) => (
                    <TableRow key={log.id || `${log.timestamp}-${log.resolved_template_id}`} data-testid={`audit-log-row-${log.id || log.timestamp}`}>
                      <TableCell data-testid={`audit-log-timestamp-${log.id || log.timestamp}`}>{formatDateTime(log.timestamp)}</TableCell>
                      <TableCell data-testid={`audit-log-doc-type-${log.id || log.timestamp}`}>{log.doc_type}</TableCell>
                      <TableCell data-testid={`audit-log-branch-${log.id || log.timestamp}`}>{locations.find((location) => location.id === log.location_id)?.name || 'Global'}</TableCell>
                      <TableCell data-testid={`audit-log-module-${log.id || log.timestamp}`}>{getModuleMeta(log.module_group).label}</TableCell>
                      <TableCell data-testid={`audit-log-template-${log.id || log.timestamp}`}>{log.resolved_template_name || '-'}</TableCell>
                      <TableCell data-testid={`audit-log-rule-${log.id || log.timestamp}`}><Badge variant="outline">{log.resolved_template_rule || '-'}</Badge></TableCell>
                      <TableCell data-testid={`audit-log-user-${log.id || log.timestamp}`}>{log.user_email || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {activePrintTab === 'catalog' && (
        <Card data-testid="print-catalog-card">
          <CardHeader>
            <CardTitle>Catálogo ERP</CardTitle>
            <CardDescription>Campos e datasources disponíveis para templates, com acesso fora do editor.</CardDescription>
          </CardHeader>
          <CardContent>
            <ErpCatalogPanel
              title="Catálogo ERP de Impressão"
              description="Pesquisa por módulo, doc_type e path directamente nas Configurações de Impressão."
              testIdPrefix="print-settings-catalog"
              editable
            />
          </CardContent>
        </Card>
      )}

      {activePrintTab === 'diagnostics' && (
        <TemplateCockpitDiagnosticsPanel
          diagnostics={templateDiagnostics}
          title="Fase 2A — diagnóstico governado da impressão"
          description="Estado físico vs efectivo, duplicações, legado por localização e warnings de presets sem tocar ainda na limpeza estrutural."
          testIdPrefix="print-cockpit-diagnostics"
        />
      )}
    </div>
    </ModuleStateBoundary>
  );
}
